import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { Integrations, init as init$2, withScope, captureException as captureException$1, setUser } from '@sentry/browser';
import Cookies from 'js-cookie';
import arLocale from 'dayjs/locale/ar';
import deLocale from 'dayjs/locale/de';
import enLocale from 'dayjs/locale/en-gb';
import enUsLocale from 'dayjs/locale/en';
import esLocale from 'dayjs/locale/es';
import frLocale from 'dayjs/locale/fr';
import idLocale from 'dayjs/locale/id';
import itLocale from 'dayjs/locale/it';
import jaLocale from 'dayjs/locale/ja';
import koLocale from 'dayjs/locale/ko';
import plLocale from 'dayjs/locale/pl';
import ptLocale from 'dayjs/locale/pt';
import ruLocale from 'dayjs/locale/ru';
import trLocale from 'dayjs/locale/tr';
import viLocale from 'dayjs/locale/vi';
import zhLocale from 'dayjs/locale/zh-cn';
import FontFaceObserver from 'fontfaceobserver';

/**
 *
 * This constant file is used to share keys across
 * our Front-end apps (HP, MP and Webapp).
 * This is pretty useful to avoid naming issues
 * between those apps when accessing
 * or storing browser storage keys.
 *
 * Each key has a name, a type and a value associated.
 * Some keys doesn't need any value the goal
 * is just to check if the key is present.
 *
 * 'LS' stands for window.localStorage
 * 'ST' stand for sessionStorage
 *
 */

/**
 * @type {String} key
 * @type {String} type
 * @type {Boolean} mandatoryValue
 * @type {Boolean} clearOnLogout
 * @type {Boolean} clearOnLogin
 * @type {Boolean} clearOnRegister
 */
const BrowserStorageConstant = {
  PAGE_VIEWED_PREMIUM: {
    key: 'PAGE_VIEWED_PREMIUM',
    type: 'LS',
    mandatoryValue: false,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  PAGE_VIEWED_PURCHASE: {
    key: 'PAGE_VIEWED_PURCHASE',
    type: 'LS',
    mandatoryValue: false,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  DISCOUNT_COUPON: {
    key: 'DISCOUNT_COUPON',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  BUSUU_PROMO_POPUP: {
    key: 'BUSUU_PROMO_POPUP',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  SELECTED_PLAN: {
    key: 'SELECTED_PLAN',
    type: 'ST',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  MARKETING_UTM: {
    key: 'MARKETING_UTM',
    type: 'ST',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: false
  },
  REGISTRATION_ONBOARDING: {
    key: 'REGISTRATION_ONBOARDING',
    type: 'ST',
    mandatoryValue: false,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: false
  },
  KEYBOARD_SHORTCUT_STATUS: {
    key: 'KEYBOARD_SHORTCUT_STATUS',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  STUDY_PLAN_VIEWED_THIS_SESSION: {
    key: 'STUDY_PLAN_VIEWED_THIS_SESSION',
    type: 'ST',
    mandatoryValue: false,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  STUDY_PLAN_TOTAL_VIEWS: {
    key: 'STUDY_PLAN_TOTAL_VIEWS',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  STUDY_PLAN_ACTIVATION_DATA: {
    key: 'STUDY_PLAN_ACTIVATION_DATA',
    type: 'ST',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  STUDY_PLAN_FORCE_DROPDOWN: {
    key: 'STUDY_PLAN_FORCE_DROPDOWN',
    type: 'ST',
    mandatoryValue: false,
    clearOnLogout: true,
    clearOnLogin: false,
    clearOnRegister: true
  },
  STUDY_PLAN_GOAL_VIEWED_THIS_SESSION: {
    key: 'STUDY_PLAN_GOAL_VIEWED_THIS_SESSION',
    type: 'ST',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  ECOMMERCE_ORIGIN: {
    key: 'ECOMMERCE_ORIGIN',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  /**
   * Used for admin tool to identify which
   * content branch the app is fetching.
   */
  CONTENT_BRANCH: {
    key: 'CONTENT_BRANCH',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  /*
   * When we send a deeplink to a non-premium user for a premium course
   * store upsell course data to use for the paywall
   */
  COURSES_OVERVIEW_FORCE_UPSELL: {
    key: 'COURSES_OVERVIEW_FORCE_UPSELL',
    type: 'ST',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Used for admin tool to show current content
   * and non ready content as well.
   */
  SHOW_ALL_CONTENT: {
    key: 'SHOW_ALL_CONTENT',
    type: 'LS',
    mandatoryValue: false,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  /**
   * Used for admin tool to disable
   * the cache on the content.
   */
  DISABLE_CONTENT_CACHE: {
    key: 'DISABLE_CONTENT_CACHE',
    type: 'LS',
    mandatoryValue: false,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  /**
   * Used for admin tool to unlock all content
   */
  UNLOCK_ALL_CONTENT: {
    key: 'UNLOCK_ALL_CONTENT',
    type: 'LS',
    mandatoryValue: false,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  /**
   * Used to clear data in media player library
   * We clear this as the library stores things like mute, volume etc...
   * and if the controls are disabled, the user ends up with a non-usable
   * player that they just assume is broken.
   */
  PLYR_STORAGE: {
    key: 'plyr',
    type: 'LS',
    mandatoryValue: false,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Handles the display of the smart-review banner on the timeline
   * It's dependant on the course language
   * See shared-data.service.js (smartReviewSetStatus) for more info
   */
  SMART_REVIEW_STATUS: {
    key: 'SMART_REVIEW_STATUS',
    type: 'ST',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Count the number of Free exercise a user did in smart-review
   * So we can show the paywall
   */
  SMART_REVIEW_NB_EXERCISES_DONE: {
    key: 'SMART_REVIEW_NB_EXERCISES_DONE',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  /**
   * Object describing the trigger status for referral signposts
   */
  REFERRAL_TRIGGERED_STATUS: {
    key: 'REFERRAL_TRIGGERED_STATUS',
    type: 'ST',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Object describing dismissal state and expiry date to reset to default
   */
  REFERRAL_DISMISSED_STATUS: {
    key: 'REFERRAL_DISMISSED_STATUS',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  /**
   * Saves the unique user referral code
   */
  REFERRAL_USER_CODE: {
    key: 'REFERRAL_USER_CODE',
    type: 'ST',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: false
  },
  /**
   * Whether or not the user has used their free language swap
   */
  USED_SINGLE_FREE_LANGUAGE_SWITCH: {
    key: 'USED_SINGLE_FREE_LANGUAGE_SWITCH',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  /**
   * Whether or not the user has interacted with a Live CTA
   * LIVE_ADD_ON_LESSONS
   */
  LIVE_ADD_ON_INTENT: {
    key: 'LIVE_ADD_ON_INTENT',
    type: 'ST',
    mandatoryValue: false,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * When a Live merch item is dismissed,
   * we store a key that has a date value in it.
   * This is used to determine whether it should be
   * shown again based on a certain timeframe.
   */
  LIVE_MERCHANDISING_DISMISSED: {
    key: 'LIVE_MERCHANDISING_DISMISSED',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: false,
    clearOnRegister: false
  },
  /**
   * Set after a successful payment
   * We can use this key on the Webapp
   * to trigger certain actions post-payment
   */
  POST_PAYMENT_TRIGGER: {
    key: 'POST_PAYMENT_TRIGGER',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: true
  },
  /**
   * Sets the live source value
   * to local storage
   */
  LIVE_SOURCE: {
    key: 'LIVE_SOURCE',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: false
  },
  /**
   * Store the state of the live cards
   * to local storage
   */
  LIVE_CARDS_STATE: {
    key: 'LIVE_CARDS_STATE',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: false
  },
  /**
   * Stores captcha token used by QA
   * to bypass captcha verification
   */
  CAPTCHA_TOKEN: {
    key: 'CAPTCHA_TOKEN',
    type: 'ST',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Stores the last touchpoint a user came from to correct an exercise
   */
  CORRECTION_SOURCE: {
    key: 'CORRECTION_SOURCE',
    type: 'ST',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Stores the motivation for learning to be used for study plan setup
   */
  MOTIVATION_FOR_LEARNING: {
    key: 'MOTIVATION_FOR_LEARNING',
    type: 'ST',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  MODAL_LIVE_B2B_STATE: {
    key: 'MODAL_LIVE_B2B_STATE',
    type: 'ST',
    mandatoryValue: true,
    clearOnLogout: false,
    clearOnLogin: false,
    clearOnRegister: false
  },
  /**
   * Stores whether the user has viewed the streak page
   */
  STREAK_PAGE_VIEWED: {
    key: 'STREAK_PAGE_VIEWED',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Stores whether the user has watched ad
   */
  AD_WATCHED: {
    key: 'AD_WATCHED',
    type: 'ST',
    mandatoryValue: false,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Stores whether the user has viewed the ads page
   */
  AD_PAGE_VIEWED: {
    key: 'AD_PAGE_VIEWED',
    type: 'ST',
    mandatoryValue: false,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Stores whether the user has viewed the lesson ready page
   */
  LESSON_READY_PAGE_VIEWED: {
    key: 'LESSON_READY_PAGE_VIEWED',
    type: 'LS',
    mandatoryValue: false,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Stores whether the user has completed the challenge today
   */
  DAILY_CHALLENGE_COMPLETED: {
    key: 'DAILY_CHALLENGE_COMPLETED',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Stores whether the user has seen the lesson ready page with the option to close it
   */
  LESSON_READY_CLOSING_VIEWED: {
    key: 'LESSON_READY_CLOSING_VIEWED',
    type: 'LS',
    mandatoryValue: false,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Stores the latest progress of dailly challenge
   */
  DAILY_CHALLENGE_LAST_RESULT: {
    key: 'DAILY_CHALLENGE_LAST_RESULT',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Stores the achieved placement test level
   */
  PLACEMENT_TEST_ACHIEVED_LEVEL: {
    key: 'PLACEMENT_TEST_ACHIEVED_LEVEL',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Stores the lesson completion status
   */
  LESSON_COMPLETION_STATUS: {
    key: 'LESSON_COMPLETION_STATUS',
    type: 'ST',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Stores whether the user has viewed the league page
   */
  LEAGUE_PAGE_VIEWED: {
    key: 'LEAGUE_PAGE_VIEWED',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Stores whether the user has viewed the smart review unlocked modal
   */
  SMART_REVIEW_UNLOCKED_MODAL_VIEWED: {
    key: 'SMART_REVIEW_UNLOCKED_MODAL_VIEWED',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  },
  /**
   * Stores whether the league tooltip has been viewed
   */
  LEAGUE_TOOLTIP_VIEWED: {
    key: 'LEAGUE_TOOLTIP_VIEWED',
    type: 'LS',
    mandatoryValue: true,
    clearOnLogout: true,
    clearOnLogin: true,
    clearOnRegister: true
  }
};

/**
 * A list of countries available for the user.
 * Would be ideal to have a single source of truth from the back-end
 * because there are differences between versions of the clients
 */
const CountriesConstants = {
  AW: {
    label: 'COUNTRY_AW',
    code: 'AW',
    callingCode: '297'
  },
  // Aruba
  AF: {
    label: 'COUNTRY_AF',
    code: 'AF',
    callingCode: '93'
  },
  // Afghanistan
  AO: {
    label: 'COUNTRY_AO',
    code: 'AO',
    callingCode: '244'
  },
  // Angola
  AI: {
    label: 'COUNTRY_AI',
    code: 'AI',
    callingCode: '1264'
  },
  // Anguilla
  AL: {
    label: 'COUNTRY_AL',
    code: 'AL',
    callingCode: '355'
  },
  // Albania
  AD: {
    label: 'COUNTRY_AD',
    code: 'AD',
    callingCode: '376'
  },
  // Andorra
  AE: {
    label: 'COUNTRY_AE',
    code: 'AE',
    callingCode: '971'
  },
  // United Arab Emirates
  AR: {
    label: 'COUNTRY_AR',
    code: 'AR',
    callingCode: '54'
  },
  // Argentina
  AM: {
    label: 'COUNTRY_AM',
    code: 'AM',
    callingCode: '374'
  },
  // Armenia
  AS: {
    label: 'COUNTRY_AS',
    code: 'AS',
    callingCode: '1684'
  },
  // American Samoa
  AG: {
    label: 'COUNTRY_AG',
    code: 'AG',
    callingCode: '1268'
  },
  // Antigua and Barbuda
  AU: {
    label: 'COUNTRY_AU',
    code: 'AU',
    callingCode: '61'
  },
  // Australia
  AT: {
    label: 'COUNTRY_AT',
    code: 'AT',
    callingCode: '43'
  },
  // Austria
  AZ: {
    label: 'COUNTRY_AZ',
    code: 'AZ',
    callingCode: '994'
  },
  // Azerbaijan
  BI: {
    label: 'COUNTRY_BI',
    code: 'BI',
    callingCode: '257'
  },
  // Burundi
  BE: {
    label: 'COUNTRY_BE',
    code: 'BE',
    callingCode: '32'
  },
  // Belgium
  BJ: {
    label: 'COUNTRY_BJ',
    code: 'BJ',
    callingCode: '229'
  },
  // Benin
  BF: {
    label: 'COUNTRY_BF',
    code: 'BF',
    callingCode: '226'
  },
  // Burkina Faso
  BD: {
    label: 'COUNTRY_BD',
    code: 'BD',
    callingCode: '880'
  },
  // Bangladesh
  BG: {
    label: 'COUNTRY_BG',
    code: 'BG',
    callingCode: '359'
  },
  // Bulgaria
  BH: {
    label: 'COUNTRY_BH',
    code: 'BH',
    callingCode: '973'
  },
  // Bahrain
  BS: {
    label: 'COUNTRY_BS',
    code: 'BS',
    callingCode: '1242'
  },
  // Bahamas
  BA: {
    label: 'COUNTRY_BA',
    code: 'BA',
    callingCode: '387'
  },
  // Bosnia and Herzegovina
  BY: {
    label: 'COUNTRY_BY',
    code: 'BY',
    callingCode: '375'
  },
  // Belarus
  BZ: {
    label: 'COUNTRY_BZ',
    code: 'BZ',
    callingCode: '501'
  },
  // Belize
  BM: {
    label: 'COUNTRY_BM',
    code: 'BM',
    callingCode: '1441'
  },
  // Bermuda
  BO: {
    label: 'COUNTRY_BO',
    code: 'BO',
    callingCode: '591'
  },
  // Bolivia
  BQ: {
    label: 'COUNTRY_BQ',
    code: 'BQ',
    callingCode: '599'
  },
  // Bonaire
  BR: {
    label: 'COUNTRY_BR',
    code: 'BR',
    callingCode: '55'
  },
  // Brazil
  BB: {
    label: 'COUNTRY_BB',
    code: 'BB',
    callingCode: '1246'
  },
  // Barbados
  BN: {
    label: 'COUNTRY_BN',
    code: 'BN',
    callingCode: '673'
  },
  // Brunei
  BT: {
    label: 'COUNTRY_BT',
    code: 'BT',
    callingCode: '975'
  },
  // Bhutan
  BW: {
    label: 'COUNTRY_BW',
    code: 'BW',
    callingCode: '267'
  },
  // Botswana
  CF: {
    label: 'COUNTRY_CF',
    code: 'CF',
    callingCode: '236'
  },
  // Central African Republic
  CA: {
    label: 'COUNTRY_CA',
    code: 'CA',
    callingCode: '1'
  },
  // Canada
  CC: {
    label: 'COUNTRY_CC',
    code: 'CC',
    callingCode: '61'
  },
  // Cocos (Keeling) Islands
  CH: {
    label: 'COUNTRY_CH',
    code: 'CH',
    callingCode: '41'
  },
  // Switzerland
  CL: {
    label: 'COUNTRY_CL',
    code: 'CL',
    callingCode: '56'
  },
  // Chile
  CN: {
    label: 'COUNTRY_CN',
    code: 'CN',
    callingCode: '86'
  },
  // China
  CI: {
    label: 'COUNTRY_CI',
    code: 'CI',
    callingCode: '225'
  },
  // Ivory Coast
  CM: {
    label: 'COUNTRY_CM',
    code: 'CM',
    callingCode: '237'
  },
  // Cameroon
  CG: {
    label: 'COUNTRY_CG',
    code: 'CG',
    callingCode: '242'
  },
  // Republic of the Congo
  CD: {
    label: 'COUNTRY_CD',
    code: 'CD',
    callingCode: '243'
  },
  // Democratic Republic of Congo
  CK: {
    label: 'COUNTRY_CK',
    code: 'CK',
    callingCode: '682'
  },
  // Cook Islands
  CO: {
    label: 'COUNTRY_CO',
    code: 'CO',
    callingCode: '57'
  },
  // Colombia
  KM: {
    label: 'COUNTRY_KM',
    code: 'KM',
    callingCode: '269'
  },
  // Comoros
  CV: {
    label: 'COUNTRY_CV',
    code: 'CV',
    callingCode: '238'
  },
  // Cape Verde
  CR: {
    label: 'COUNTRY_CR',
    code: 'CR',
    callingCode: '506'
  },
  // Costa Rica
  CU: {
    label: 'COUNTRY_CU',
    code: 'CU',
    callingCode: '53'
  },
  // Cuba
  CW: {
    label: 'COUNTRY_CW',
    code: 'CW',
    callingCode: '599'
  },
  // Curaçao
  CX: {
    label: 'COUNTRY_CX',
    code: 'CX',
    callingCode: '61'
  },
  // Christmas Island
  KY: {
    label: 'COUNTRY_KY',
    code: 'KY',
    callingCode: '1345'
  },
  // Cayman Islands
  CY: {
    label: 'COUNTRY_CY',
    code: 'CY',
    callingCode: '357'
  },
  // Cyprus
  CZ: {
    label: 'COUNTRY_CZ',
    code: 'CZ',
    callingCode: '420'
  },
  // Czechia
  DE: {
    label: 'COUNTRY_DE',
    code: 'DE',
    callingCode: '49'
  },
  // Germany
  DJ: {
    label: 'COUNTRY_DJ',
    code: 'DJ',
    callingCode: '253'
  },
  // Djibouti
  DM: {
    label: 'COUNTRY_DM',
    code: 'DM',
    callingCode: '1767'
  },
  // Dominica
  DK: {
    label: 'COUNTRY_DK',
    code: 'DK',
    callingCode: '45'
  },
  // Denmark
  DO: {
    label: 'COUNTRY_DO',
    code: 'DO',
    callingCode: '1'
  },
  // Dominican Republic
  DZ: {
    label: 'COUNTRY_DZ',
    code: 'DZ',
    callingCode: '213'
  },
  // Algeria
  EC: {
    label: 'COUNTRY_EC',
    code: 'EC',
    callingCode: '593'
  },
  // Ecuador
  EG: {
    label: 'COUNTRY_EG',
    code: 'EG',
    callingCode: '20'
  },
  // Egypt
  ER: {
    label: 'COUNTRY_ER',
    code: 'ER',
    callingCode: '291'
  },
  // Eritrea
  EH: {
    label: 'COUNTRY_EH',
    code: 'EH',
    callingCode: '212'
  },
  // Western Sahara
  ES: {
    label: 'COUNTRY_ES',
    code: 'ES',
    callingCode: '34'
  },
  // Spain
  EE: {
    label: 'COUNTRY_EE',
    code: 'EE',
    callingCode: '372'
  },
  // Estonia
  ET: {
    label: 'COUNTRY_ET',
    code: 'ET',
    callingCode: '251'
  },
  // Ethiopia
  FI: {
    label: 'COUNTRY_FI',
    code: 'FI',
    callingCode: '358'
  },
  // Finland
  FJ: {
    label: 'COUNTRY_FJ',
    code: 'FJ',
    callingCode: '679'
  },
  // Fiji
  FK: {
    label: 'COUNTRY_FK',
    code: 'FK',
    callingCode: '500'
  },
  // Falkland Islands
  FR: {
    label: 'COUNTRY_FR',
    code: 'FR',
    callingCode: '33'
  },
  // France
  FO: {
    label: 'COUNTRY_FO',
    code: 'FO',
    callingCode: '298'
  },
  // Faroe Islands
  GA: {
    label: 'COUNTRY_GA',
    code: 'GA',
    callingCode: '241'
  },
  // Gabon
  GB: {
    label: 'COUNTRY_GB',
    code: 'GB',
    callingCode: '44'
  },
  // United Kingdom
  GE: {
    label: 'COUNTRY_GE',
    code: 'GE',
    callingCode: '995'
  },
  // Georgia
  GG: {
    label: 'COUNTRY_GG',
    code: 'GG',
    callingCode: '44'
  },
  // Guernsey
  GH: {
    label: 'COUNTRY_GH',
    code: 'GH',
    callingCode: '233'
  },
  // Ghana
  GI: {
    label: 'COUNTRY_GI',
    code: 'GI',
    callingCode: '350'
  },
  // Gibraltar
  GN: {
    label: 'COUNTRY_GN',
    code: 'GN',
    callingCode: '224'
  },
  // Guinea
  GP: {
    label: 'COUNTRY_GP',
    code: 'GP',
    callingCode: '590'
  },
  // Guadeloupe
  GM: {
    label: 'COUNTRY_GM',
    code: 'GM',
    callingCode: '220'
  },
  // Gambia
  GW: {
    label: 'COUNTRY_GW',
    code: 'GW',
    callingCode: '245'
  },
  // Guinea-Bissau
  GQ: {
    label: 'COUNTRY_GQ',
    code: 'GQ',
    callingCode: '240'
  },
  // Equatorial Guinea
  GR: {
    label: 'COUNTRY_GR',
    code: 'GR',
    callingCode: '30'
  },
  // Greece
  GD: {
    label: 'COUNTRY_GD',
    code: 'GD',
    callingCode: '1473'
  },
  // Grenada
  GL: {
    label: 'COUNTRY_GL',
    code: 'GL',
    callingCode: '299'
  },
  // Greenland
  GT: {
    label: 'COUNTRY_GT',
    code: 'GT',
    callingCode: '502'
  },
  // Guatemala
  GF: {
    label: 'COUNTRY_GF',
    code: 'GF',
    callingCode: '594'
  },
  // French Guiana
  GU: {
    label: 'COUNTRY_GU',
    code: 'GU',
    callingCode: '1671'
  },
  // Guam
  GY: {
    label: 'COUNTRY_GY',
    code: 'GY',
    callingCode: '592'
  },
  // Guyana
  HK: {
    label: 'COUNTRY_HK',
    code: 'HK',
    callingCode: '852'
  },
  // Hong Kong
  HN: {
    label: 'COUNTRY_HN',
    code: 'HN',
    callingCode: '504'
  },
  // Honduras
  HR: {
    label: 'COUNTRY_HR',
    code: 'HR',
    callingCode: '385'
  },
  // Croatia
  HT: {
    label: 'COUNTRY_HT',
    code: 'HT',
    callingCode: '509'
  },
  // Haiti
  HU: {
    label: 'COUNTRY_HU',
    code: 'HU',
    callingCode: '36'
  },
  // Hungary
  ID: {
    label: 'COUNTRY_ID',
    code: 'ID',
    callingCode: '62'
  },
  // Indonesia
  IM: {
    label: 'COUNTRY_IM',
    code: 'IM',
    callingCode: '44'
  },
  // Isle of Man
  IN: {
    label: 'COUNTRY_IN',
    code: 'IN',
    callingCode: '91'
  },
  // India
  IO: {
    label: 'COUNTRY_IO',
    code: 'IO',
    callingCode: '246'
  },
  // British Indian Ocean Territory
  IE: {
    label: 'COUNTRY_IE',
    code: 'IE',
    callingCode: '353'
  },
  // Ireland
  IR: {
    label: 'COUNTRY_IR',
    code: 'IR',
    callingCode: '98'
  },
  // Iran
  IQ: {
    label: 'COUNTRY_IQ',
    code: 'IQ',
    callingCode: '964'
  },
  // Iraq
  IS: {
    label: 'COUNTRY_IS',
    code: 'IS',
    callingCode: '354'
  },
  // Iceland
  IL: {
    label: 'COUNTRY_IL',
    code: 'IL',
    callingCode: '972'
  },
  // Israel
  IT: {
    label: 'COUNTRY_IT',
    code: 'IT',
    callingCode: '39'
  },
  // Italy
  JM: {
    label: 'COUNTRY_JM',
    code: 'JM',
    callingCode: '1876'
  },
  // Jamaica
  JE: {
    label: 'COUNTRY_JE',
    code: 'JE',
    callingCode: '44'
  },
  // Jersey
  JO: {
    label: 'COUNTRY_JO',
    code: 'JO',
    callingCode: '962'
  },
  // Jordan
  JP: {
    label: 'COUNTRY_JP',
    code: 'JP',
    callingCode: '81'
  },
  // Japan
  KZ: {
    label: 'COUNTRY_KZ',
    code: 'KZ',
    callingCode: '7'
  },
  // Kazakhstan
  KE: {
    label: 'COUNTRY_KE',
    code: 'KE',
    callingCode: '254'
  },
  // Kenya
  KG: {
    label: 'COUNTRY_KG',
    code: 'KG',
    callingCode: '996'
  },
  // Kyrgyzstan
  KH: {
    label: 'COUNTRY_KH',
    code: 'KH',
    callingCode: '855'
  },
  // Cambodia
  KI: {
    label: 'COUNTRY_KI',
    code: 'KI',
    callingCode: '686'
  },
  // Kiribati
  KN: {
    label: 'COUNTRY_KN',
    code: 'KN',
    callingCode: '1869'
  },
  // Saint Kitts and Nevis
  KR: {
    label: 'COUNTRY_KR',
    code: 'KR',
    callingCode: '82'
  },
  // South Korea
  XK: {
    label: 'COUNTRY_XK',
    code: 'XK',
    callingCode: '383'
  },
  // Kosovo
  KW: {
    label: 'COUNTRY_KW',
    code: 'KW',
    callingCode: '965'
  },
  // Kuwait
  LA: {
    label: 'COUNTRY_LA',
    code: 'LA',
    callingCode: '856'
  },
  // Laos
  LB: {
    label: 'COUNTRY_LB',
    code: 'LB',
    callingCode: '961'
  },
  // Lebanon
  LR: {
    label: 'COUNTRY_LR',
    code: 'LR',
    callingCode: '231'
  },
  // Liberia
  LY: {
    label: 'COUNTRY_LY',
    code: 'LY',
    callingCode: '218'
  },
  // Libya
  LC: {
    label: 'COUNTRY_LC',
    code: 'LC',
    callingCode: '1758'
  },
  // Saint Lucia
  LI: {
    label: 'COUNTRY_LI',
    code: 'LI',
    callingCode: '423'
  },
  // Liechtenstein
  LK: {
    label: 'COUNTRY_LK',
    code: 'LK',
    callingCode: '94'
  },
  // Sri Lanka
  LS: {
    label: 'COUNTRY_LS',
    code: 'LS',
    callingCode: '266'
  },
  // Lesotho
  LT: {
    label: 'COUNTRY_LT',
    code: 'LT',
    callingCode: '370'
  },
  // Lithuania
  LU: {
    label: 'COUNTRY_LU',
    code: 'LU',
    callingCode: '352'
  },
  // Luxembourg
  LV: {
    label: 'COUNTRY_LV',
    code: 'LV',
    callingCode: '371'
  },
  // Latvia
  MO: {
    label: 'COUNTRY_MO',
    code: 'MO',
    callingCode: '853'
  },
  // Macau
  MA: {
    label: 'COUNTRY_MA',
    code: 'MA',
    callingCode: '212'
  },
  // Morocco
  MC: {
    label: 'COUNTRY_MC',
    code: 'MC',
    callingCode: '377'
  },
  // Monaco
  MD: {
    label: 'COUNTRY_MD',
    code: 'MD',
    callingCode: '373'
  },
  // Moldova
  MG: {
    label: 'COUNTRY_MG',
    code: 'MG',
    callingCode: '261'
  },
  // Madagascar
  MV: {
    label: 'COUNTRY_MV',
    code: 'MV',
    callingCode: '960'
  },
  // Maldives
  MX: {
    label: 'COUNTRY_MX',
    code: 'MX',
    callingCode: '52'
  },
  // Mexico
  MH: {
    label: 'COUNTRY_MH',
    code: 'MH',
    callingCode: '692'
  },
  // Marshall Islands
  MK: {
    label: 'COUNTRY_MK',
    code: 'MK',
    callingCode: '389'
  },
  // Macedonia
  ML: {
    label: 'COUNTRY_ML',
    code: 'ML',
    callingCode: '223'
  },
  // Mali
  MT: {
    label: 'COUNTRY_MT',
    code: 'MT',
    callingCode: '356'
  },
  // Malta
  MM: {
    label: 'COUNTRY_MM',
    code: 'MM',
    callingCode: '95'
  },
  // Myanmar
  ME: {
    label: 'COUNTRY_ME',
    code: 'ME',
    callingCode: '382'
  },
  // Montenegro
  MN: {
    label: 'COUNTRY_MN',
    code: 'MN',
    callingCode: '976'
  },
  // Mongolia
  MP: {
    label: 'COUNTRY_MP',
    code: 'MP',
    callingCode: '1670'
  },
  // Northern Mariana Islands
  MZ: {
    label: 'COUNTRY_MZ',
    code: 'MZ',
    callingCode: '258'
  },
  // Mozambique
  MR: {
    label: 'COUNTRY_MR',
    code: 'MR',
    callingCode: '222'
  },
  // Mauritania
  MS: {
    label: 'COUNTRY_MS',
    code: 'MS',
    callingCode: '1664'
  },
  // Montserrat
  MQ: {
    label: 'COUNTRY_MQ',
    code: 'MQ',
    callingCode: '596'
  },
  // Martinique
  MU: {
    label: 'COUNTRY_MU',
    code: 'MU',
    callingCode: '230'
  },
  // Mauritius
  MW: {
    label: 'COUNTRY_MW',
    code: 'MW',
    callingCode: '265'
  },
  // Malawi
  MY: {
    label: 'COUNTRY_MY',
    code: 'MY',
    callingCode: '60'
  },
  // Malaysia
  YT: {
    label: 'COUNTRY_YT',
    code: 'YT',
    callingCode: '262'
  },
  // Mayotte
  NA: {
    label: 'COUNTRY_NA',
    code: 'NA',
    callingCode: '264'
  },
  // Namibia
  NC: {
    label: 'COUNTRY_NC',
    code: 'NC',
    callingCode: '687'
  },
  // New Caledonia
  NE: {
    label: 'COUNTRY_NE',
    code: 'NE',
    callingCode: '227'
  },
  // Niger
  NF: {
    label: 'COUNTRY_NF',
    code: 'NF',
    callingCode: '672'
  },
  // Norfolk Island
  NG: {
    label: 'COUNTRY_NG',
    code: 'NG',
    callingCode: '234'
  },
  // Nigeria
  NI: {
    label: 'COUNTRY_NI',
    code: 'NI',
    callingCode: '505'
  },
  // Nicaragua
  NL: {
    label: 'COUNTRY_NL',
    code: 'NL',
    callingCode: '31'
  },
  // Netherlands
  NO: {
    label: 'COUNTRY_NO',
    code: 'NO',
    callingCode: '47'
  },
  // Norway
  NP: {
    label: 'COUNTRY_NP',
    code: 'NP',
    callingCode: '977'
  },
  // Nepal
  NR: {
    label: 'COUNTRY_NR',
    code: 'NR',
    callingCode: '674'
  },
  // Nauru
  NZ: {
    label: 'COUNTRY_NZ',
    code: 'NZ',
    callingCode: '64'
  },
  // New Zealand
  OM: {
    label: 'COUNTRY_OM',
    code: 'OM',
    callingCode: '968'
  },
  // Oman
  PK: {
    label: 'COUNTRY_PK',
    code: 'PK',
    callingCode: '92'
  },
  // Pakistan
  PA: {
    label: 'COUNTRY_PA',
    code: 'PA',
    callingCode: '507'
  },
  // Panama
  PN: {
    label: 'COUNTRY_PN',
    code: 'PN',
    callingCode: '64'
  },
  // Pitcairn Islands
  PE: {
    label: 'COUNTRY_PE',
    code: 'PE',
    callingCode: '51'
  },
  // Peru
  PH: {
    label: 'COUNTRY_PH',
    code: 'PH',
    callingCode: '63'
  },
  // Philippines
  PW: {
    label: 'COUNTRY_PW',
    code: 'PW',
    callingCode: '680'
  },
  // Palau
  PG: {
    label: 'COUNTRY_PG',
    code: 'PG',
    callingCode: '675'
  },
  // Papua New Guinea
  PL: {
    label: 'COUNTRY_PL',
    code: 'PL',
    callingCode: '48'
  },
  // Poland
  PR: {
    label: 'COUNTRY_PR',
    code: 'PR',
    callingCode: '1'
  },
  // Puerto Rico
  KP: {
    label: 'COUNTRY_KP',
    code: 'KP',
    callingCode: '850'
  },
  // North Korea
  PT: {
    label: 'COUNTRY_PT',
    code: 'PT',
    callingCode: '351'
  },
  // Portugal
  PY: {
    label: 'COUNTRY_PY',
    code: 'PY',
    callingCode: '595'
  },
  // Paraguay
  PS: {
    label: 'COUNTRY_PS',
    code: 'PS',
    callingCode: '970'
  },
  // Palestine
  PF: {
    label: 'COUNTRY_PF',
    code: 'PF',
    callingCode: '689'
  },
  // French Polynesia
  QA: {
    label: 'COUNTRY_QA',
    code: 'QA',
    callingCode: '974'
  },
  // Qatar
  RE: {
    label: 'COUNTRY_RE',
    code: 'RE',
    callingCode: '262'
  },
  // Réunion
  RO: {
    label: 'COUNTRY_RO',
    code: 'RO',
    callingCode: '40'
  },
  // Romania
  RU: {
    label: 'COUNTRY_RU',
    code: 'RU',
    callingCode: '7'
  },
  // Russia
  RW: {
    label: 'COUNTRY_RW',
    code: 'RW',
    callingCode: '250'
  },
  // Rwanda
  SA: {
    label: 'COUNTRY_SA',
    code: 'SA',
    callingCode: '966'
  },
  // Saudi Arabia
  SD: {
    label: 'COUNTRY_SD',
    code: 'SD',
    callingCode: '249'
  },
  // Sudan
  SN: {
    label: 'COUNTRY_SN',
    code: 'SN',
    callingCode: '221'
  },
  // Senegal
  SG: {
    label: 'COUNTRY_SG',
    code: 'SG',
    callingCode: '65'
  },
  // Singapore
  SJ: {
    label: 'COUNTRY_SJ',
    code: 'SJ',
    callingCode: '4779'
  },
  // Svalbard and Jan Mayen
  SB: {
    label: 'COUNTRY_SB',
    code: 'SB',
    callingCode: '677'
  },
  // Solomon Islands
  SL: {
    label: 'COUNTRY_SL',
    code: 'SL',
    callingCode: '232'
  },
  // Sierra Leone
  SV: {
    label: 'COUNTRY_SV',
    code: 'SV',
    callingCode: '503'
  },
  // El Salvador
  SM: {
    label: 'COUNTRY_SM',
    code: 'SM',
    callingCode: '378'
  },
  // San Marino
  SO: {
    label: 'COUNTRY_SO',
    code: 'SO',
    callingCode: '252'
  },
  // Somalia
  PM: {
    label: 'COUNTRY_PM',
    code: 'PM',
    callingCode: '508'
  },
  // Saint Pierre and Miquelon
  RS: {
    label: 'COUNTRY_RS',
    code: 'RS',
    callingCode: '381'
  },
  // Serbia
  ST: {
    label: 'COUNTRY_ST',
    code: 'ST',
    callingCode: '239'
  },
  // São Tomé and Príncipe
  SR: {
    label: 'COUNTRY_SR',
    code: 'SR',
    callingCode: '597'
  },
  // Suriname
  SK: {
    label: 'COUNTRY_SK',
    code: 'SK',
    callingCode: '421'
  },
  // Slovakia
  SI: {
    label: 'COUNTRY_SI',
    code: 'SI',
    callingCode: '386'
  },
  // Slovenia
  SE: {
    label: 'COUNTRY_SE',
    code: 'SE',
    callingCode: '46'
  },
  // Sweden
  SZ: {
    label: 'COUNTRY_SZ',
    code: 'SZ',
    callingCode: '268'
  },
  // Swaziland
  SX: {
    label: 'COUNTRY_SX',
    code: 'SX',
    callingCode: '599'
  },
  // Sint Maarten
  SS: {
    label: 'COUNTRY_SS',
    code: 'SS',
    callingCode: '211'
  },
  // South Sudan
  SC: {
    label: 'COUNTRY_SC',
    code: 'SC',
    callingCode: '248'
  },
  // Seychelles
  SY: {
    label: 'COUNTRY_SY',
    code: 'SY',
    callingCode: '963'
  },
  // Syria
  TC: {
    label: 'COUNTRY_TC',
    code: 'TC',
    callingCode: '1649'
  },
  // Turks and Caicos Islands
  TD: {
    label: 'COUNTRY_TD',
    code: 'TD',
    callingCode: '235'
  },
  // Chad
  TG: {
    label: 'COUNTRY_TG',
    code: 'TG',
    callingCode: '228'
  },
  // Togo
  TH: {
    label: 'COUNTRY_TH',
    code: 'TH',
    callingCode: '66'
  },
  // Thailand
  TJ: {
    label: 'COUNTRY_TJ',
    code: 'TJ',
    callingCode: '992'
  },
  // Tajikistan
  TK: {
    label: 'COUNTRY_TK',
    code: 'TK',
    callingCode: '690'
  },
  // Tokelau
  TM: {
    label: 'COUNTRY_TM',
    code: 'TM',
    callingCode: '993'
  },
  // Turkmenistan
  TL: {
    label: 'COUNTRY_TL',
    code: 'TL',
    callingCode: '670'
  },
  // Timor-Leste
  TO: {
    label: 'COUNTRY_TO',
    code: 'TO',
    callingCode: '676'
  },
  // Tonga
  TT: {
    label: 'COUNTRY_TT',
    code: 'TT',
    callingCode: '1868'
  },
  // Trinidad and Tobago
  TN: {
    label: 'COUNTRY_TN',
    code: 'TN',
    callingCode: '216'
  },
  // Tunisia
  TR: {
    label: 'COUNTRY_TR',
    code: 'TR',
    callingCode: '90'
  },
  // Turkey
  TV: {
    label: 'COUNTRY_TV',
    code: 'TV',
    callingCode: '688'
  },
  // Tuvalu
  TW: {
    label: 'COUNTRY_TW',
    code: 'TW',
    callingCode: '886'
  },
  // Taiwan
  TZ: {
    label: 'COUNTRY_TZ',
    code: 'TZ',
    callingCode: '255'
  },
  // Tanzania
  UG: {
    label: 'COUNTRY_UG',
    code: 'UG',
    callingCode: '256'
  },
  // Uganda
  UA: {
    label: 'COUNTRY_UA',
    code: 'UA',
    callingCode: '380'
  },
  // Ukraine
  UY: {
    label: 'COUNTRY_UY',
    code: 'UY',
    callingCode: '598'
  },
  // Uruguay
  US: {
    label: 'COUNTRY_US',
    code: 'US',
    callingCode: '1'
  },
  // United States
  UZ: {
    label: 'COUNTRY_UZ',
    code: 'UZ',
    callingCode: '998'
  },
  // Uzbekistan
  VA: {
    label: 'COUNTRY_VA',
    code: 'VA',
    callingCode: '379'
  },
  // Vatican City
  VC: {
    label: 'COUNTRY_VC',
    code: 'VC',
    callingCode: '1784'
  },
  // Saint Vincent and the Grenadines
  VE: {
    label: 'COUNTRY_VE',
    code: 'VE',
    callingCode: '58'
  },
  // Venezuela
  VG: {
    label: 'COUNTRY_VG',
    code: 'VG',
    callingCode: '1284'
  },
  // British Virgin Islands
  VI: {
    label: 'COUNTRY_VI',
    code: 'VI',
    callingCode: '1340'
  },
  // United States Virgin Islands
  VN: {
    label: 'COUNTRY_VN',
    code: 'VN',
    callingCode: '84'
  },
  // Vietnam
  VU: {
    label: 'COUNTRY_VU',
    code: 'VU',
    callingCode: '678'
  },
  // Vanuatu
  WF: {
    label: 'COUNTRY_WF',
    code: 'WF',
    callingCode: '681'
  },
  // Wallis and Futuna
  WS: {
    label: 'COUNTRY_WS',
    code: 'WS',
    callingCode: '685'
  },
  // Samoa
  YE: {
    label: 'COUNTRY_YE',
    code: 'YE',
    callingCode: '967'
  },
  // Yemen
  ZA: {
    label: 'COUNTRY_ZA',
    code: 'ZA',
    callingCode: '27'
  },
  // South Africa
  ZM: {
    label: 'COUNTRY_ZM',
    code: 'ZM',
    callingCode: '260'
  },
  // Zambia
  ZW: {
    label: 'COUNTRY_ZW',
    code: 'ZW',
    callingCode: '263'
  } // Zimbabwe
};

/**
 * A list of country codes protected by GDPR
 */
const GDPR_COUNTRY_CODES = [
// EU Countries
'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
// Others (UK + Channel Islands)
'GB', 'GG', 'JE', 'IM',
// EEA Countries
'AD', 'IS', 'LI', 'NO', 'CH',
// Others (Outside Europe)
'AR', 'CA', 'FO', 'IL', 'NZ', 'UY', 'JP', 'KR'];

const eCommerceOriginConstants = {
  CERTIFICATE_PAYWALL: 'certificate',
  CLAIM_BASIC_FT_PAYWALL_BANNER: 'claim_basic_ft_paywall_banner',
  CLAIM_BASIC_TRIAL_BANNER: 'claim_basic_trial_banner',
  CLAIM_REFERRAL_FT_PAYWALL_BANNER: 'claim_referral_ft_paywall_banner',
  CLAIM_REFERRAL_TRIAL_BANNER: 'claim_referral_trial_banner',
  DISCOUNT_COUPON_FORM: 'discount_coupon_form',
  EXTERNAL_SOURCES: 'external_sources',
  FREE_TRIAL_ONBOARDING: 'free_trial_onboarding',
  LESSONS_LOCKED: 'lesson_locked',
  MEMORISE_ACTIVITY_PAYWALL: 'memorise',
  MERCHANDISING_BANNER: 'merch_banner',
  MERCHANDISING_BANNER_COUNTDOWN: 'merch_banner_countdown',
  ONBOARDING_STUDY_PLAN: 'onboarding_study_plan',
  ONBOARDING_STUDY_PLAN_LAST_CHANCE: 'onboarding_study_plan_last_chance',
  PREMIUM_ACTIVITY_TOOLTIP: 'locked_content',
  PREMIUM_CTA_NAVTOP: 'cta_navtop',
  PRINT_PREMIUM_PAYWALL: 'lesson_summary',
  PROMO_BRAZE: 'promo_braze',
  PROMO_CART_ABANDONMENT: 'cart_abandonment',
  PROMO_GLOBAL: 'promo_global',
  PROMO_TWO_DAY_STREAK: '2_day_streak',
  SMART_REVIEW_ACTIVITY_PAYWALL: 'smart_review',
  UPGRADE_BANNER: 'upgrade_banner',
  FRICTION_WITH_ADS: 'friction_with_ads',
  ONBOARDING_PUSH_NOTIFICATIONS: 'onboarding_push_notifications',
  TIMELINE_BANNER: 'timeline_banner',
  HOMEPAGE_MERCH_BANNER: 'homepage_merch_banner',
  HOMEPAGE_BANNER: 'homepage_banner',
  PAID_SEARCH_BANNER: 'paid_search_banner',
  SEO_BANNER: 'seo_banner',
  UNLOCK_ALL_LESSONS: 'unlock_all_lessons',
  FRICTION_WITH_REWARDED_ADS: 'friction_with_rewarded_ads'
};

/* eslint-disable no-console */
/**
 * Display a warning message in the console
 * @param {String} fileName - file name where log is coming from
 * @param {String} message - warning to display
 * @param {Multiple} data - can be any additional data (optional)
 * @param {Function} callback - (optional)
 */
function log$1(fileName, message, data, callback) {
  const logMessage = `[File: ${fileName}] ${message}`;
  if (console && console.warn) {
    if (data) {
      console.warn(logMessage, data);
    } else {
      console.warn(logMessage);
    }
  }
  if (callback && typeof callback === 'function') {
    callback({
      message: logMessage,
      data
    });
  }
}
var ConsoleHelper = {
  log: log$1
};

/* eslint-disable valid-typeof */

/**
 * Indicates whether or not variable is an Array
 * @param  {ANY_TYPE} variable
 * @return {Boolean}
 * @public
 */
function isArray(variable) {
  return Array.isArray(variable);
}

/**
 * Indicates whether or not variable is an Object
 * @param  {ANY_TYPE} variable
 * @return {Boolean}
 * @public
 */
function isObject(variable) {
  return Object.prototype.toString.call(variable) === '[object Object]';
}

/**
 * Indicates whether or not variable is typeof a type
 * @param  {ANY_TYPE} variable
 * @param  {ANY_TYPE} type
 * @return {Boolean}
 * @public
 */
function isTypeOf(variable, type) {
  return Boolean(typeof variable === type);
}

/**
 * Indicates whether or not a variable is a DOM/HTML element
 * @param  {ANY_TYPE} variable
 * @return {Boolean}
 */
function isDOMElement(variable) {
  return Boolean(variable instanceof Element);
}

/**
 * Convert a Node list or HTMLCollection to a real Array
 * @param  {Collection}
 * @return {Array}
 * @private
 */
function toArray(collection) {
  const newArray = [];
  if (!collection) {
    return newArray;
  }
  for (let i = 0, l = collection.length; i < l; i++) {
    newArray.push(collection[i]);
  }
  return newArray;
}

/**
 * Returns a given id element
 * @param  {String}
 * @return {Element || null}
 * @public
 */
function getById(id) {
  return document.getElementById(id) || null;
}

/**
 * Returns elements based on a given classname
 * @param  {String} selector
 * @param  {Element} parentNode
 * @return {Array}
 * @public
 */
function getElementsByClass(selector) {
  let parent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document;
  const elements = parent.querySelectorAll(`.${selector}`);
  return toArray(elements);
}

/**
 * Returns element based on a given classname
 * @param  {String} selector
 * @param  {Element} parentNode
 * @return {Element}
 * @public
 */
function getElementByClass(selector) {
  let parent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document;
  return parent.querySelector(`.${selector}`) || null;
}

/**
 * Returns elements based on a given attribute
 * @param  {String} attribute
 * @param  {Element} parentNode
 * @return {Array}
 * @public
 */
function getElementsByAttribute(attribute) {
  let parent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document;
  const elements = parent.querySelectorAll(`[${attribute}]`);
  return toArray(elements);
}

/**
 * Returns element based on a given attribute
 * @param  {String} attribute
 * @param  {Element} parentNode
 * @return {Element}
 * @public
 */
function getElementByAttribute(attribute) {
  let parent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document;
  return parent.querySelector(`[${attribute}]`) || null;
}

/**
 * Trigger custom event, cross browser compatible
 * createEvent and initEvent are deprecated but working on IE11
 */
function triggerEvent(eventName, eventTarget) {
  let toBubble = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  let event = null;
  if (typeof Event === 'function') {
    event = new Event(eventName, {
      bubbles: toBubble
    });
  } else {
    event = document.createEvent('Event');
    event.initEvent(eventName, true, true);
  }
  eventTarget.dispatchEvent(event);
}

/**
 * React behaves differently when it comes to setting a value in a text input.
 * Referenced from: https://github.com/facebook/react/issues/10135#issuecomment-314441175
 * The answer is set in the element:
 * - For React: checks the existence of the 'value' property & setter
 * - Otherwise: sets the value directly
 */
function setNativeValue(element, value) {
  const valueProperty = Object.getOwnPropertyDescriptor(element, 'value');
  if (valueProperty) {
    const valueSetter = Object.getOwnPropertyDescriptor(element, 'value').set;
    const prototype = Object.getPrototypeOf(element);
    const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value').set;
    if (valueSetter && valueSetter !== prototypeValueSetter) {
      prototypeValueSetter.call(element, value);
    } else {
      valueSetter.call(element, value);
    }
  } else {
    element.value = value;
  }
}

/**
 * Find the closest parent element based on a className
 * up to the document. Returns null if not found.
 * @param  {string} className
 * @param  {element} child
 */
function getParentByClass(className, child) {
  const parent = child.parentNode;
  if (parent === document) {
    return null;
  }
  if (hasClass(parent, className)) {
    return parent;
  }
  return getParentByClass(className, parent);
}

/**
 * DOM Manipulation Shortcuts
 * @private
 */
function _forEachClass(string, callback) {
  const array = string.split(' ');
  for (let i = 0, l = array.length; i < l; i++) {
    callback(array[i], i);
  }
}

/**
 * Check an existing class on a given element
 * @param  {Element}  elem
 * @param  {String}   className
 * @return {Boolean}
 * @public
 */
function hasClass(elem, className) {
  return elem && elem.classList && elem.classList.contains(className);
}

/**
 * Add a class on element (multiples classes supported)
 * @param {Element} elem
 * @param {String} classes
 * @public
 */
function addClass(elem, classes) {
  _forEachClass(classes, className => {
    elem.classList.add(className);
  });
}

/**
 * Remove a class on element (multiples classes supported)
 * @param  {Element} elem
 * @param  {String}  classes
 * @public
 */
function removeClass(elem, classes) {
  _forEachClass(classes, className => {
    elem.classList.remove(className);
  });
}

/**
 * Toggle a class on element (multiples classes supported)
 * @param  {Element} elem
 * @param  {String}  classes
 * @public
 */
function toggleClass(elem, classes) {
  _forEachClass(classes, className => {
    elem.classList.toggle(className);
  });
}

/**
 * Display a warning message in the console
 * @param {String} fileName    - file name where log is coming from
 * @param {String} message     - warning to display
 * @param {Multiple} datas     - can be any additional datas (optional)
 * @param {Function} callback  - (optional)
 * @public
 */
function log(fileName, message, datas, callback) {
  ConsoleHelper.log(fileName, message, datas, callback);
}

/**
 * A boolean to check if env is localhost
 * @return {Boolean}
 * @public
 */
function isLocalEnvironment() {
  const location = getWindowLocation();
  if (!location) {
    return false;
  }
  const {
    hostname
  } = location;
  const locals = ['localhost', 'local.internal.busuu.com', 'local-hp.internal.busuu.com'];
  return Boolean(locals.indexOf(hostname) !== -1);
}

/**
 * A boolean to check if the current
 * environment is production.
 * (www.busuu.com || www.boshu.cn || org.busuu.com || slate.internal.busuu.com)
 * @return {boolean}
 */
function isProduction() {
  const location = getWindowLocation();
  if (!location) {
    return false;
  }
  const currentHostname = location.hostname;
  const hostnames = ['www.busuu.com', 'www.boshu.cn', 'org.busuu.com', 'slate.internal.busuu.com'];
  return hostnames.some(hostname => hostname === currentHostname);
}

/**
 * A boolean to check if the current
 * environment is china.
 * (www.boshu.cn || integration-13.internal.busuu.com)
 * @return {boolean}
 */
function isCurrentDomainChina() {
  const location = getWindowLocation();
  if (!location) {
    return false;
  }
  const currentHostname = location.hostname;
  const chinaHostnames = ['www.boshu.cn', 'integration-13.internal.busuu.com'];
  return chinaHostnames.some(hostname => hostname === currentHostname);
}

/**
 * Clean multiples white spaces and line breaks from a given string
 * @param  {String}
 * @return {String}
 * @public
 */
function clean(str) {
  return str.trim().replace(/\s+/g, ' ');
}

/**
 * Toggle show/hide an element
 * @param  {Element} elem
 * @public
 */
function toggleShow(elem) {
  if (elem.style.display === 'block') {
    elem.style.display = 'none';
  } else {
    elem.style.display = 'block';
  }
}

/**
 * Hide an element using CSS
 * @param  {Element} elem
 * @public
 */
function hide(elem) {
  elem.style.display = 'none';
}

/**
 * Show an element using css
 * @param  {Element} elem
 * @public
 */
function show(elem) {
  elem.style.display = 'block';
}

/**
 * Return the text value from an element without html
 * @param  {Element} - elem
 * @return {String}
 * @public
 */
function getText(elem) {
  return elem.textContent || elem.innerText;
}

/**
 * Set text value on a given element
 * @param  {Element} - elem
 * @return {String} - text
 * @public
 */
function setText(elem, text) {
  if (elem.textContent) {
    elem.textContent = text;
  } else if (elem.innerText) {
    elem.innerText = text; // IE
  } else {
    const _text = document.createTextNode(text);
    elem.appendChild(_text);
  }
}

/**
 * Return a parameter value from the current URL
 * @param  {String} - parameter
 * @param {String} - key to use in location object (search/hash etc...)
 * @return {String || null}
 * @public
 * NB: We have a key switcher for Angular as there's a # in the URL the search value is always empty
 */
function getParameter(param) {
  let key = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'search';
  const location = getWindowLocation();
  if (!location) {
    return null;
  }

  // Get search portion of url without `?`
  const query = location[key].split('?')[1];
  if (query) {
    // Create array with all parameters in form of variable=value
    const parameters = query.split('&');
    for (let i = 0, l = parameters.length; i < l; i++) {
      /**
       * Split the parameter so the first index contains the name
       * of variable and the second one the value
       */
      const currentParameter = parameters[i].split('=');
      if (currentParameter[0] === param) {
        return currentParameter[1]; // Return parameter value
      }
    }
  }
  return null; // Return null when not found
}

/**
 * Parse query after HP redirects to Webapp with reversed query position
 * e.g ?course=course_pack_en_travel&lang=en#/redirect/select_course
 * returns course=course_pack_en_travel&lang=en
 * @param {String} href
 */
function getQuery(href) {
  const removeHash = query => {
    const queryParts = query.split('#');
    return queryParts.length ? queryParts[0] : query;
  };
  const linkParts = href && href.split('?');
  const query = linkParts && linkParts.length >= 2 ? removeHash(linkParts[1]) : null;
  return query;
}

/**
 * Delegates an Event on a given Element
 * @param  {Element}   - elem handler
 * @param  {String}    - className target
 * @param  {Event}     - evt name
 * @param  {Function}  - callback
 * @public
 */
function delegate(elem, className, evt, callback) {
  elem.addEventListener(evt, e => {
    let _target = e.target;
    while (_target && _target !== elem) {
      if (hasClass(_target, className)) {
        callback.call(_target, e);
        break;
      }
      _target = _target.parentNode;
    }
  });
}

/**
 * Format a given number to readable human format
 * e.g.: 1 > 1.00
 *       -1 > -1.00
 *       10 > 10.00
 *       99.85 > 99.85
 * @param  {Integer || String}
 * @return {String}
 * @public
 */
function formatNumber(num) {
  let i = parseFloat(num);
  if (isNaN(i)) {
    i = 0.0;
  }
  let s = i.toString();
  if (s.indexOf('.') < 0) {
    s += '.00';
  }
  if (s.indexOf('.') == s.length - 2) {
    s += '0';
  }
  return s.toString();
}

/**
 * Recursively clone an Object or an Array.
 * Does a deep cloning and avoid object reference.
 * @param  {Object || Array} - collection
 * @return {Object || Array}
 * @public
 */
function deepClone(collection) {
  const cloneObject = function (object) {
    const obj = {};
    for (const prop in object) {
      if (object.hasOwnProperty(prop)) {
        const value = object[prop];
        if (isObject(value) || isArray(value)) {
          obj[prop] = deepClone(value);
        } else {
          obj[prop] = value;
        }
      }
    }
    return obj;
  };
  const cloneArray = function (array) {
    const arr = [];
    for (let i = 0, l = array.length; i < l; i++) {
      const value = array[i];
      if (isObject(value) || isArray(value)) {
        arr.push(deepClone(value));
      } else {
        arr.push(value);
      }
    }
    return arr;
  };
  if (isObject(collection)) {
    return cloneObject(collection);
  }
  if (isArray(collection)) {
    return cloneArray(collection);
  }
  throw new Error("'collection' argument must be an Object or an Array");
}

/**
 * Return the caret position inside a given element
 * @param  {Element} element - DOM element (input, textarea)
 * @return {Integer}
 * @public
 */
function getCaretPosition(element) {
  if (!element) {
    return 0;
  }
  if ('selectionStart' in element) {
    return element.selectionStart;
  }
  if (document.selection) {
    // IE
    element.focus();
    const sel = document.selection.createRange();
    const selLen = sel.text.length;
    sel.moveStart('character', -element.value.length);
    return sel.text.length - selLen;
  }
  return 0;
}

/**
 * Set the caret position inside a given element
 * @param  {Element} element - DOM element (input, textarea)
 * @return {Integer}
 * @public
 */
function setCaretPosition(element, position) {
  if (!element) {
    return false;
  }
  if (element.createTextRange) {
    const range = element.createTextRange();
    range.move('character', position);
    range.select();
  } else if (element.selectionStart) {
    element.focus();
    setTimeout(() => {
      element.setSelectionRange(position, position);
    }, 0);
  } else {
    element.focus();
  }
}

/**
 * Check if the given parent element has the given child as descendant
 * @param {Element} parent
 * @param {Element} child
 * @public
 */
function hasChild(parent, child) {
  let parentChild = child.parentNode;
  while (parentChild !== null) {
    if (parentChild === parent) {
      return true;
    }
    parentChild = parentChild.parentNode;
  }
  return false;
}

/**
 * Indicates whether or not a given
 * value is a finite number
 * @param  {all} value
 * @return {Boolean}
 */
function _isFinite(value) {
  return typeof value === 'number' && window.isFinite(value);
}

/**
 * Indicates whether or not a given
 * value is a number
 * @param  {all} value
 * @return {Boolean}
 */
function isNumber(value) {
  return _isFinite(value) && !isNaN(value);
}

/**
 * Will check if the value is numeric (nb: 1 or '1' will return true)
 * @param {String|Number} value
 * @return {Boolean}
 */
function isNumeric(value) {
  return window.isFinite(value) && !window.isNaN(value);
}

/**
 * Returns user timezone offset from UTC based on internal clock
 * We default to zero hours (UTC) if this fails
 * NB: getTimezoneOffset always returns the 'inverted'
 * value of what we'd expect, eg:
 * - PST (GMT - 8) returns 480 minutes (8 hours behind GMT)
 * - NZDT (GMT + 13) returns -780 minutes (13 hours ahead of GMT)
 * We want the opposite in hours, so we always divide our timezone by 60 and multiply it by -1.
 * @return {Integer} timezone offset
 */
function getTimezoneOffsetFromUTC() {
  const date = new Date();
  const timezone = date.getTimezoneOffset() || 0;
  if (timezone === 0) {
    return timezone;
  }
  return timezone / 60 * -1;
}

/**
 * The API expects our timezone offset to be formatted in
 * a specific way: (+/-)00:00
 * @param {Integer} timezone
 * @return {String} timezone in format (+/-)00:00
 */
function formatTimezoneOffset(timezone) {
  if (!isNumber(timezone)) {
    throw new Error('timezone value must be a number');
  }
  let hours = null;
  let minutes = null;
  const timezoneIsNegative = Boolean(timezone < 0);
  if (timezone === 0) {
    return '+00:00';
  }

  // If timezone is negative, multiply by -1 to always set a positive timezone
  if (timezoneIsNegative) {
    timezone *= -1;
  }

  // Set hours
  if (timezone > 0 && timezone < 10) {
    hours = `0${Math.floor(timezone)}`;
  } else {
    hours = Math.floor(timezone);
  }

  // Set minutes - get any decimals
  const decimals = timezone - Math.floor(timezone);
  switch (decimals) {
    case 0.25:
      minutes = '15';
      break;
    case 0.5:
      minutes = '30';
      break;
    case 0.75:
      minutes = '45';
      break;
    default:
      minutes = '00';
  }

  // Append (+/-) based on whether original timezone was positive or negative
  hours = timezoneIsNegative ? `-${hours}` : `+${hours}`;
  return `${hours}:${minutes}`;
}

/**
 * Converts accents from a given string to their
 * closest sibling.
 * Example: 'Crème brulée à chaud ça régale' > 'Creme brulee a chaud ca regale'
 * @param {string} string
 * @return {string}
 */
function convertAccents(string) {
  const normalizedString = string.normalize('NFD');
  const valueWithoutSpecialCharacters = normalizedString.replace(/[\u0300-\u036f]/g, '');
  return valueWithoutSpecialCharacters;
}

/**
 * Removes special characters from a given string
 * Example:
 * - removeSpecialCharacters('Crème_Brul(ée) {à} chaud ç@-régale!') > 'CrmeBrulechaudrgale'
 * - removeSpecialCharacters('Crème_Brul(ée) {à} chaud ç@-régale!', 'è_ç@-') > 'Crème_Brulechaudç@-rgale'
 * NB: Preserve words from A to Z but consider accents as special characters.
 * @param {string} string
 * @param {string} exceptions - A string with special characters whitelist
 * @return {string}
 */
function removeSpecialCharacters(string) {
  let exceptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  /**
   * Ensure the exceptions doesn't have any special
   * Regexp characters so we escape them using `\`
   */
  exceptions = exceptions.split('').join('\\');

  // Create regexp replacement rule
  const replace = `[^A-Za-z${exceptions}]`;

  // Return string without special characters
  const regexp = new RegExp(replace, 'g');
  return string.replace(regexp, '');
}

/**
 * Takes an element from a given index in an array
 * and moves it to the end of the given array.
 * @param  {Array} array
 * @param  {Index} index
 * @return {Array}
 */
function moveToEndOfArray(array) {
  let index = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  const newArray = deepClone(array);
  const removedEl = newArray.splice(index, 1)[0];
  newArray.push(removedEl);
  return newArray;
}

/**
 * Generates a Universal Unique Identifier (UUID).
 * Source: https://www.w3resource.com/javascript-exercises/javascript-math-exercise-23.php
 * @return {String}
 */
function generateUUID() {
  let dt = new Date().getTime();
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c == 'x' ? r : r & 0x3 | 0x8).toString(16);
  });
  return uuid;
}
function getBrowserLanguage() {
  if (!window.navigator) {
    return 'en';
  }
  return window.navigator.language || window.navigator.userLanguage || 'en';
}

/**
 * Type checker, matches two objects.
 * Compares an object with a similar object which
 * checks that the values of each property match a given type.
 *
 * NB: 'strict' also checks the lengths of the objects to ensure
 * that they are exactly equal.
 *
 * Takes the following types:
 * `type_string`
 * `type_number`
 * `type_boolean`
 * `type_function`
 * `type_null`
 * `type_undefined`
 * `type_object`
 * `type_array`
 *
 * Can be invoked as such:
 *
 * verifyPropTypes({
 *     foo: true,
 *     bar: 123,
 *     baz: 'hello'
 * }, {
 *     foo: 'type_boolean',
 *     bar: 'type_number',
 *     baz: 'type_string'
 * }, true);
 *
 * @param  {Object} obj
 * @param  {Object} schema
 * @param  {Boolean} strict
 * @return {Boolean}
 */
function verifyPropTypes() {
  let obj = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let schema = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let strict = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  const schemaMatchesObj = Object.entries(schema).every(_ref => {
    let [key, type] = _ref;
    // Check that key exists in object
    if (!obj.hasOwnProperty(key)) {
      return false;
    }

    // Check that type matches
    const value = obj[key];
    switch (type) {
      case 'type_string':
      case 'type_boolean':
      case 'type_function':
        return isTypeOf(value, type.substr(5));
      case 'type_number':
        return isNumber(value);
      case 'type_object':
        return isObject(value);
      case 'type_array':
        return isArray(value);
      case 'type_null':
      case 'type_undefined':
        return Boolean(String(value) === type.substr(5));
      case 'type_element':
        return isDOMElement(value);
      case 'type_number_or_string':
        return isNumber(value) || isTypeOf(value, 'string');
      default:
        throw new Error(`verifyPropTypes() - type '${type}' unknown`);
    }
  });

  // Also check length matches if strict mode
  if (strict) {
    const lengthCheck = Boolean(Object.keys(obj).length === Object.keys(schema).length);
    return Boolean(schemaMatchesObj && lengthCheck);
  }
  return Boolean(schemaMatchesObj);
}

/**
 * Safety check on the location object before performing operations on it
 * @return {Object || null}
 * @private
 */
function getWindowLocation() {
  const {
    location
  } = window;
  return location || null;
}

/**
 * Object.freeze() is shallow
 * This method recursively freezes nested objects
 * @param  {Object} data - object to freeze
 * @return {Object} Frozen object
 * @public
 */
function deepFreeze() {
  let data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  if (!isObject(data)) {
    return data;
  }
  Object.values(data).forEach(value => {
    if (isObject(value) && !Object.isFrozen(value)) {
      deepFreeze(value);
    }
  });
  return Object.freeze(data);
}
var Utils = {
  toArray,
  isArray,
  isObject,
  isTypeOf,
  getById,
  getElementsByClass,
  getElementByClass,
  getElementsByAttribute,
  getElementByAttribute,
  triggerEvent,
  getParentByClass,
  hasClass,
  addClass,
  removeClass,
  toggleClass,
  log,
  isLocalEnvironment,
  isCurrentDomainChina,
  isProduction,
  clean,
  toggleShow,
  hide,
  show,
  getText,
  setText,
  getParameter,
  getQuery,
  delegate,
  formatNumber,
  deepClone,
  getCaretPosition,
  setCaretPosition,
  hasChild,
  isFinite: _isFinite,
  isNumber,
  isNumeric,
  getTimezoneOffsetFromUTC,
  formatTimezoneOffset,
  convertAccents,
  removeSpecialCharacters,
  moveToEndOfArray,
  setNativeValue,
  generateUUID,
  getBrowserLanguage,
  verifyPropTypes,
  deepFreeze
};

/**
 * Date Utils:
 * List of formats that can be used with dayjs can be found here:
 * https://github.com/iamkun/dayjs/blob/dev/docs/en/API-reference.md#list-of-all-available-formats
 */

/**
 * Human readable days
 * NB: Index ordered - new Date().getDay() returns 0 index for Sunday
 * NB2: We never serve these strings to the user (as they're not localised)
 * but our APIs sometimes return these date strings so we can compare against.
 * @type {Array}
 * @private
 */
const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Returns a new instance of dayjs that can be used
 * to perform various actions
 * @return {Object} dayjs Instance
 * @public
 */
const getNewInstance = () => {
  return dayjs();
};

/**
 * Formats a UNIX timestamp to a human readable format
 *
 * Input: timestampToDate(1575373577, 'DD/MM/YYYY')
 * Output: '03/12/2019'
 *
 * @param  {Number} timestamp
 * @param  {String} format
 * @return {String}
 * @public
 */
const timestampToDate = function (timestamp) {
  let format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'DD MMMM YYYY';
  return dayjs.unix(timestamp).format(format);
};

/**
 * Formats a date to a UNIX timestamp
 * @param  {Object} date
 * @return {Integer} timestamp
 * @public
 */
const dateToTimestamp = date => {
  return dayjs(date).unix();
};

/**
 * Converts a numerical date or date object to an object
 *
 * Input: dateToObject(2018-10-20)
 * Output: {
 *     day: 20,
 *     month: 9, // index
 *     year: 2018
 * }
 *
 * @param  {String} numerical date | type: 2018-10-20
 * @return {Object}
 * @public
 */
const dateToObject = numericalDate => {
  const date = dayjs(numericalDate);
  return {
    day: date.get('date'),
    month: date.get('month'),
    year: date.get('year')
  };
};

/**
 * Formats a numerical date or date object to any given format
 * Docs: https://github.com/iamkun/dayjs/blob/dev/docs/en/API-reference.md#displaying
 *
 * Input: dateToFormat(2019-12-03, 'ddd')
 * Output: 'Tue'
 *
 * @param  {String} numerical date | type: 2018-10-20
 * @param  {String} format
 * @return {String}
 * @public
 */
const dateToFormat = function (numericalDate) {
  let format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'ddd';
  return dayjs(numericalDate).format(format);
};

/**
 * Gets the locale translation for a given month
 *
 * NB: Locale needs to be set in the app first or this
 * will just default to dayjs default ('en').
 * Set this on app init with the `date-time-locale.service.js`
 *
 * Input: getMonthLocaleValue(2, 'MMMM');
 * Output: 'March'
 *
 * @param  {Number} monthIndex (0 is Jan, 1 is Feb etc...)
 * @return {String}
 * @public
 */
const getMonthLocaleValue = function (monthIndex) {
  let format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'MMMM';
  return dayjs().month(monthIndex).format(format);
};

/**
 * Returns a weekday name *not a translation/localisation*
 * This is useful as the APIs sometimes use these values,
 * for eg: `monday: selected`
 *
 * Input: getWeekdayFromNumericalDate('2019-12-03')
 * Output: 'tuesday'
 *
 * @param  {String} numericalDate
 * @return {String}
 * @public
 */
const getWeekdayFromNumericalDate = numericalDate => {
  const dayIndex = dayjs(numericalDate).day();
  return weekdays[dayIndex];
};

/**
 * Returns a date range string from two ISO dates
 * eg => Mar 21 - Mar 25
 *
 * @param  {String} unit      - what unit we want to range
 * @param  {String} format    - format we want the dates returned in
 * @return {String} dateRange - eg. Mar 21 - Mar 25
 * @public
 */
const getDateRangeString = function () {
  let unit = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'week';
  let format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'MMM D';
  const now = dayjs();
  const startOfDate = now.startOf(unit);
  const endOfDate = now.endOf(unit);
  const from = dayjs(startOfDate).format(format);
  const to = dayjs(endOfDate).format(format);
  return `${from} - ${to}`;
};

/**
 * Returns a number based on a given date.
 *
 * For eg (comparing weeks):
 *     Today is '2018-08-08' (Wednesday - current week)
 *     Comparing date is '2018-08-03' (Friday - previous week)
 *     => We'd return `2` as we're on Week 2.
 *
 * @param  {String} date | iso date string ie. '2018-01-01'
 * @param  {String} unit | 'week', 'month' etc...
 * @return {Number}
 * @public
 */
const getUnitNumberFromDate = function (date) {
  let unit = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'week';
  const today = dayjs();
  const fromDate = dayjs(date);
  const startOfThisWeek = today.startOf(unit);
  const startOfWeekFromDate = fromDate.startOf(unit);
  return startOfThisWeek.diff(startOfWeekFromDate, unit) + 1;
};

/**
 * Returns a generic usable array we can use
 * for date selectors
 * NB: We shift sunday to the end of our array
 * before returning as we want to always display
 * Monday first in any list.
 *
 * NB: Data object param is optional and can be used
 * to pass existing data to be mapped in the below format.
 * If not passed, we assume all values are false.
 *
 * {
 *     monday: true,
 *     tuesday: false
 *     ...
 * }
 *
 * Returns an array with following structure:
 * [
 *     {
 *         name: 'monday',
 *         selected: true,
 *         label: 'Mon'
 *     },
 *     {
 *         name: 'tuesday',
 *         selected: false,
 *         label: 'Tue'
 *     }
 * ]
 *
 * @param  {String} format
 * @return {Array}
 * @public
 */
const getUsableDaySelectorData = function () {
  let format = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'ddd';
  const days = weekdays.map((dayName, index) => {
    const label = dayjs().day(index).format(format);
    return {
      label,
      selected: true,
      name: dayName
    };
  });
  return Utils.moveToEndOfArray(days, 0);
};

/**
 * Return whether two days are within a certain number
 * of hours from each other
 *
 * @param {String | Date} date1
 * @param {String | Date} date2
 * @param {Number} hours
 */
const getDateIsWithinHours = (date1, date2, hours) => {
  const start = dayjs(date1);
  const end = dayjs(date2);
  const hoursBetween = start.diff(end, 'hour');
  return Boolean(hoursBetween < hours);
};

/**
 * Returns a new Date object from a date plus the amount of days
 * @param {String | Date} date
 * @param {Number} days
 * @returns {Date}
 */
const addDaysToDate = function (date) {
  let days = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  return dayjs(date).add(days, 'days').toDate();
};

/**
 * Returns a new Date object from a date plus the amount of months
 * @param {String | Date} date
 * @param {Number} months
 * @returns {Date}
 */
const addMonthsToDate = function (date) {
  let months = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  return dayjs(date).add(months, 'months').toDate();
};
var dateUtils = {
  getNewInstance,
  timestampToDate,
  dateToTimestamp,
  dateToObject,
  dateToFormat,
  getMonthLocaleValue,
  getWeekdayFromNumericalDate,
  getDateRangeString,
  getUnitNumberFromDate,
  getUsableDaySelectorData,
  getDateIsWithinHours,
  addDaysToDate,
  addMonthsToDate
};

/* eslint-disable no-undef */

/**
 * Creates a file from a blob
 * @param  {Object} bits | blob properties
 * @param  {String} filename
 * @return {File}
 */
const createFileFromBlob = (bits, filename) => {
  return new File([bits], filename);
};

/**
 * Helper to know when the buildFormData should recurse
 * For eg, we don't want this to recurse over strings, dates, files etc...
 * @param  {Object} data
 * @return {Boolean}
 */
const shouldRecurse = data => {
  const isObjectOrArray = Boolean(Utils.isObject(data) || Utils.isArray(data));
  const isNotDate = !(data instanceof Date);
  const isNotFile = !(data instanceof File);
  const isNotBlob = !(data instanceof Blob);
  return Boolean(isObjectOrArray && isNotDate && isNotFile && isNotBlob);
};

/**
 *
 * Takes initial JSON and appends it to our formData constructor.
 *
 * Initial JSON:
 * {
 *     name: 'str',
 *     nullable: null,
 *     date: new Date(),
 *     another_object: {
 *         name: 'my_name',
 *         value: 'whatever'
 *     },
 *     my_array: [
 *         {
 *             key1: {
 *                 name: 'key1'
 *             }
 *         }
 *     ]
 * }
 *
 * ...gets appended to our formData constructor as such:
 *
 * formData.append(name, 'str');
 * formData.append(nullable: '');
 * formData.append(date: 'Tue Jun 25 2019 15:34:45 GMT+0100 (British Summer Time)');
 * formData.append(another_object[name]: 'my_name');
 * formData.append(another_object[value]: 'whatever');
 * formData.append(my_array[0][key1][name]: 'key1');
 *
 *
 * @param  {Constructor} formData
 * @param  {Object} data
 * @param  {String} parentKey
 */
const buildFormData = _ref => {
  let {
    blobFilename,
    formData,
    data,
    parentKey
  } = _ref;
  if (data && shouldRecurse(data)) {
    Object.keys(data).forEach(key => {
      buildFormData({
        blobFilename,
        formData,
        data: data[key],
        parentKey: parentKey ? `${parentKey}[${key}]` : key
      });
    });
  } else {
    let value = data === null ? '' : data;

    // Check for any Blobs, convert them to files
    if (value instanceof Blob && blobFilename) {
      value = createFileFromBlob(value, blobFilename);
    }
    formData.append(parentKey, value);
  }
};
var jsonToFormData = {
  buildFormData
};

/* eslint-disable consistent-return */

// Private vars
const filename$6 = 'script-loader.js';
const firstScriptTag = document.getElementsByTagName('script')[0];
const queue = [];
const scriptListIDs = [];
let randomId = 0;
let windowIsLoad = false;

/**
 * Script page injection
 * @param  {String}   url
 * @param  {Function} callback
 * @param  {String}   id
 */
const load = (url, id, callback) => {
  const script = document.createElement('script');
  script.async = true;
  script.src = url;
  script.id = id;
  if (callback && script.readyState) {
    // IE
    script.onreadystatechange = () => {
      if (script.readyState === 'loaded' || script.readyState === 'complete') {
        script.onreadystatechange = null;
        callback(id);
      }
    };
  } else if (callback) {
    // others
    script.onload = () => {
      callback(id);
    };
  }
  if (firstScriptTag && firstScriptTag.parentNode) {
    firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
  }
};

/**
 * Add a Script into the wait queue if window isn't loaded
 * If window is loaded, inject the script immediatly.
 * @param {String}   url      - Script URL
 * @param {String}   id       - Script ID (optional)
 * @param {Function} callback - Invoked when script is loaded (optional)
 * @param {Boolean}  loadNow  - Force Script to load right now (optional)
 */
const add = (url, id, callback, loadNow) => {
  // Avoid duplicate script ids
  if (scriptListIDs.includes(id)) {
    ConsoleHelper.log(filename$6, `Script id "${id}" is duplicated, abort dom injection.`);
    return false;
  }

  // Generate an id for each script
  let scriptID = id;
  if (!scriptID) {
    scriptID = `script-${randomId}`;
    randomId += 1;
  }

  // ..and push it into the list
  scriptListIDs.push(scriptID);

  // Scripts can be injected right now or after load event (default)
  if (loadNow || windowIsLoad) {
    load(url, scriptID, callback);
  } else {
    /**
     * If window isn't loaded,
     * then store current arguments.
     * This will be invoked later when window will be loaded
     */
    const array = [];
    array.push(url);
    array.push(scriptID);
    array.push(callback);
    queue.push(array);
  }
};

/**
 * window on 'load' event handler
 */
const onLoad = () => {
  windowIsLoad = true;

  // For each element in the queue, load it
  for (let i = 0, l = queue.length; i < l; i++) {
    load.apply(null, queue[i]);
  }

  // Empty queue
  queue.length = 0;
};

// Attach a load event on window
window.addEventListener('load', onLoad);

// Public methods
var scriptLoader = {
  add
};

/* eslint-disable no-undef */

/**
 * Indicates whether or not a value is a String
 * @param  {ANY_TYPE} value
 * @return {Boolean}
 */
const isString = value => {
  return Object.prototype.toString.call(value) === '[object String]';
};

/**
 * Replace all variations of '<br>' with `\n`
 * @param  {String} string - original string to replace
 * @return {String} string without the <br> tags
 */
const replaceBreakTagsWithNewLines = string => {
  if (!isString(string)) {
    return '';
  }
  return string.replace(/<br\s*\/?>/g, '\n');
};

/**
 * Replaces any real linebreaks with `<br/>` tags.
 * This is done to not lose formatting.
 * @param  {String} string
 * @return {String} string without the linebreaks
 */
const replaceNewLinesWithBreakTags = string => {
  if (!isString(string)) {
    return '';
  }
  return string.replace(/\n|\r\n|\r/g, '<br>');
};
const sanitize = string => {
  if (!isString(string)) {
    return '';
  }
  return DOMPurify.sanitize(string);
};

/**
 * Indicates wether or not a value is a valid URL
 * @param  {String} string
 * @return {Boolean}
 * Source: https://stackoverflow.com/questions/5717093/check-if-a-javascript-string-is-a-url
 */
const isValidURL = string => {
  if (!isString(string)) {
    return false;
  }
  let url;
  const allowedProtocols = ['http:', 'https:'];
  const allowedHosts = ['www.busuu.com', 'busuu.com', 'live.busuu.com'];
  try {
    url = new URL(string);
    const {
      protocol
    } = url;
    // if unvalid protocol mark url as unvalid automatically
    if (protocol && !allowedProtocols.includes(protocol)) return false;
    // if in dev or test env only check for valid protocol
    if (window.location.hostname === 'localhost') {
      return protocol && allowedProtocols.includes(protocol);
    }
    // If in a live domain prod env check the domain hosting the file with the url we want to redirect to
    return url.host === window.location.host || allowedHosts.includes(url.host);
  } catch (e) {
    return false;
  }
};
var stringHelper = {
  replaceBreakTagsWithNewLines,
  replaceNewLinesWithBreakTags,
  isString,
  isValidURL,
  sanitize
};

/**
 * Calculates difference between two timestamps in seconds
 * @param  {Timestamp} time
 * @return {Timestamp}
 */
const calculateTimeDifference = time => {
  if (!Utils.isNumber(time)) {
    throw new Error(`calculateTimeDifference() - 'time: ${time}' - is not a number.`);
  }
  const nowInSeconds = Math.round(new Date().getTime() / 1000);
  return time - nowInSeconds;
};

/**
 * Adds a leading `0` for numbers under `10`
 * @param  {Number} num
 * @return {String}
 */
const padStartNumber = num => {
  if (!Utils.isNumber(num)) {
    throw new Error(`padStartNumber() - 'num: ${num}' - is not a number.`);
  }
  if (num < 10) {
    return `0${num}`;
  }
  return num.toString();
};

/**
 * Formats time in seconds to a human readable string format.
 * @param  {Number} seconds - Time in seconds to format
 * @return {String} Time in DD:HH:MM:SS | HH:MM:SS | MM:SS format
 */
const secsToHumanReadable = seconds => {
  if (!seconds || !Utils.isNumber(seconds) || seconds === 0) {
    return '00:00';
  }
  const dividedMins = seconds % 3600;
  const remainingDays = padStartNumber(Math.floor(seconds / 86400));
  const remainingHours = padStartNumber(Math.floor(seconds % 86400 / (60 * 60)));
  const remainingMins = padStartNumber(Math.floor(dividedMins / 60));
  const secondsRemaining = padStartNumber(Math.ceil(dividedMins % 60));
  if (remainingDays > 0) {
    return `${remainingDays}:${remainingHours}:${remainingMins}:${secondsRemaining}`;
  }
  if (remainingHours > 0) {
    return `${remainingHours}:${remainingMins}:${secondsRemaining}`;
  }
  return `${remainingMins}:${secondsRemaining}`;
};
var timeHelper = {
  calculateTimeDifference,
  secsToHumanReadable
};

/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable no-undef */
/**
 * Ensure a given promise rejects or resolves
 * before a certain amount of time (in milliseconds).
 * src: https://italonascimento.github.io/applying-a-timeout-to-your-promises/
 * @param  {Promise} promise
 * @param  {Number}  ms
 * @return {Promise}
 */
const timeoutPromise = function (promise) {
  let ms = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 3000;
  let timer = null;
  const timeout = new Promise((resolve, reject) => {
    timer = window.setTimeout(() => {
      reject({
        type: 'PROMISE_TIMED_OUT',
        message: `Promise didn't complete under ${ms}ms.`
      });
    }, ms);
  });
  const onSuccess = result => {
    window.clearTimeout(timer);
    return result;
  };
  const onError = error => {
    window.clearTimeout(timer);
    throw error;
  };
  return Promise.race([promise, timeout]).then(onSuccess).catch(onError);
};

const BASE_USER_MOCK = {
  premium_data: {
    product: 'Premium',
    created: 1588171229,
    started: 1588171229,
    next_charge: null,
    expiration: null,
    cancelled: null,
    platform: null,
    productId: null,
    market: null,
    subscriptionType: null,
    type: null,
    subscriptionId: null,
    current_plan: null,
    upgrade_plan: null,
    suspended_plan: null,
    partner: null,
    partner_metadata: null,
    in_app_cancellation: false,
    subscription_status: null
  },
  uid: 110,
  has_avatar: false,
  avatar: 'https://randomuser.me/api/portraits/women/11.jpg',
  avatar_variations: {
    small: 'https://dkiiqzqghqrn.cloudfront.net/files/default-avatars/neutral-small.jpg',
    large: 'https://dkiiqzqghqrn.cloudfront.net/files/default-avatars/neutral-medium.jpg',
    '500_500': 'https://dkiiqzqghqrn.cloudfront.net/files/default-avatars/neutral-large.jpg',
    original: 'https://dkiiqzqghqrn.cloudfront.net/files/default-avatars/neutral-large.jpg'
  },
  friends: 0,
  thumbs: {
    made: 0,
    received: 0,
    total: 0,
    percentage: 0,
    likes_received: 0
  },
  exercises_done: 0,
  corrections_done: 0,
  best_corrections_awarded: 0,
  name: 'Leanne-B2C',
  city: null,
  about_me: 'I like to talk about myself',
  languages: {
    interface: 'en',
    learning_default: 'en',
    learning: {
      en: 'beginner'
    },
    spoken: {
      en: 'beginner'
    }
  },
  country: 'Spain',
  country_code: 'ES',
  flag: 'https://cdn.busuu.com/assets/8f5f0ae2/images/flags/CX.png',
  shadow_ban: false,
  spoken_language_chosen: false,
  is_premium: true,
  currency_code: 'JPY',
  timezone: 'Z',
  last_login: 1583258179,
  created: 1558716621,
  last_access: 1583258189,
  email: 'leanne+3@busuu.com',
  phone_data: {},
  unique_username: null,
  access_token: null,
  fb_uid: null,
  g_uid: null,
  apple_user_id: null,
  opt_in_promotions: false,
  berries: 0,
  profile_name: null,
  birthday: null,
  checklist_complete: true,
  occupation: null,
  notification_correction_received: true,
  notification_correction_added: true,
  notification_correction_requested: true,
  notification_correction_replies: true,
  notification_friend_requested: true,
  notification_friend_replied: true,
  notification_mute_all: false,
  private_mode_friend_requested: false,
  notification_study_plan: false,
  premium_provider: null,
  course_access: {
    travel: false,
    business: [],
    mobile: []
  },
  roles: [43, 25],
  placement_chooser: false,
  placement_test_available: {
    de: true,
    es: true,
    fr: true,
    it: true,
    pt: true,
    en: false
  },
  free_trial_eligible: true,
  access: {
    tier: 'plus'
  },
  referral_token: 'blZzM0p1Wm4yTDg9',
  referral_url: 'https://get-integration-01.internal.busuu.com/blZzM0p1Wm4yTDg9',
  current_course_pack: 'course_pack_en_uber',
  institutions: [],
  is_friend: null
};

/* eslint-disable */
const filename$5 = 'snowplow-script.js';
const SP_SDK_LOCATION = 'https://cdn.busuu.com/busuu-v2.12.0.js';
const SP_ENDPOINT_PROD = 'events.busuu.com';
const SP_ENDPOINT_INTEGRATION = 'events-integration.busuu.com';
const appConfig = {
  homepage: {
    trackerName: 'busuu_homepage_tracker',
    appId: 'busuu_homepage'
  },
  webapp: {
    trackerName: 'busuu_webapp_tracker',
    appId: 'busuu_webapp'
  },
  org: {
    trackerName: 'busuu_mp_tracker',
    appId: 'busuu_mp'
  }
};

/**
 * @return {String}
 * @private
 */
const getEndpoint = () => {
  return Utils.isProduction() ? SP_ENDPOINT_PROD : SP_ENDPOINT_INTEGRATION;
};

/**
 * Snowplow setup
 * @param {String} appType
 * @return {Object}
 * @private
 */
const getSnowplowSetup = appType => {
  return {
    namespace: 'newTracker',
    trackerName: appConfig[appType].trackerName,
    endpoint: getEndpoint()
  };
};

/**
 * Snowplow settings
 * @param {String} appType
 * @return {Object}
 * @private
 */
const getSnowplowSettings = appType => {
  return {
    forceSecureTracker: true,
    discoverRootDomain: true,
    userFingerprint: true,
    appId: appConfig[appType].appId
  };
};

/**
 * Snowplow setup
 * source: https://github.com/snowplow/snowplow/wiki/1-General-parameters-for-the-Javascript-tracker
 * @param {String} appType
 */
const loadSnowplow = appType => {
  (function (p, l, o, w, i, n, g) {
    if (!p[i]) {
      p.GlobalSnowplowNamespace = p.GlobalSnowplowNamespace || [];
      p.GlobalSnowplowNamespace.push(i);
      p[i] = function () {
        (p[i].q = p[i].q || []).push(arguments);
      };
      p[i].q = p[i].q || [];
      n = l.createElement(o);
      g = l.getElementsByTagName(o)[0];
      n.async = 1;
      n.src = w;
      g.parentNode.insertBefore(n, g);
    }
  })(window, document, 'script', SP_SDK_LOCATION, 'snowplow');
  const snowplowSetup = getSnowplowSetup(appType);
  const snowplowSettings = getSnowplowSettings(appType);
  window.snowplow(snowplowSetup.namespace, snowplowSetup.trackerName, snowplowSetup.endpoint, snowplowSettings);
};

/**
 * Their documentation recommend asyncronous integration over syncronous
 * https://github.com/snowplow/snowplow/wiki/integrating-javascript-tags-onto-your-website
 * We initially tried splitting their script so that we could queue events and wait until
 * DOM load before injecting their script. However this caused errors and prevented the
 * custom event tracker from occasionally being fired so we opted to go with their
 * recommended initialisation script.
 * See: https://github.com/snowplow/snowplow/issues/3169
 */
const init$1 = appType => {
  if (!appType || !appConfig[appType]) {
    ConsoleHelper.log(filename$5, 'init() - missing param `appType`, cannot load Snowplow script');
    return false;
  }
  loadSnowplow(appType);
};
var scriptSnowplow = {
  init: init$1
};

/* eslint-disable func-names */

// Private vars
const filename$4 = 'exception-service.js';

/**
 * Indicates whether or not Sentry has been initialised first.
 * @type {Boolean}
 * @private
 */
let initialised = false;

/**
 * Returns a Sentry Integration instance
 * Doc: https://docs.sentry.io/platforms/javascript/#sdk-integrations
 * @param {String} integrationName
 * @param {Object} options
 * @public
 */
const getSentryIntegrationInstance = function (integrationName) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return new Integrations[integrationName](options);
};

/**
 * Returns global config for Sentry
 * @param {String} key
 * @param {Boolean} enableLocal
 * @param {Object} options
 * @return {Object}
 * @private
 */
const getConfig = (key, enableLocal, options) => {
  const opts = Utils.deepClone(options);
  const config = {
    dsn: key,
    allowUrls: [/cdn\.busuu\.com/],
    // production
    ...opts
  };
  if (enableLocal) {
    delete config.allowUrls;
  }
  return config;
};

/**
 * @private
 */
const isSentryInitialised = () => {
  if (!initialised) {
    Utils.log(filename$4, 'Sentry is not initialised.');
  }
  return initialised;
};

/**
 * Write down into a textarea all the logs that happened
 * on the page. This is for QA purpose.
 * @param {String} value
 * @private
 */
const setHTMLtoAppConsole = value => {
  const consoleElement = Utils.getById('console-output');
  if (consoleElement) {
    consoleElement.value = `${consoleElement.value + JSON.stringify(value)}\n\n`;
  }
};

/**
 * Returns the user info config for Sentry
 * @return {Object} userConfig
 * @private
 */
const getUserContext = user => {
  return {
    id: user.uid,
    username: user.name,
    email: user.email,
    countryCode: user.country_code
  };
};

/**
 * We only manually capture exceptions as not to overload
 * Sentry with all kinds of errors and go over our allowance.
 * @param  {Error}
 * @param  {Object} additional data
 * @private
 */
const captureException = (error, data) => {
  if (!isSentryInitialised()) {
    return false;
  }
  withScope(function (scope) {
    if (Utils.isObject(data)) {
      scope.setExtras(data);
    }
    captureException$1(error);
  });
};

/**
 * Logs an exception in the console
 * Also optionally sends an error to Sentry (this is enabled by default
 * for errors)
 *
 * Can be used as such:
 *
 * ExceptionService.handle('error', {
 *     filename: 'my.component.js',
 *     message: 'myFunction() - Something went wrong',
 *     data: {...},
 *     sendToSentry: false
 * });
 *
 * @param  {String} type - 'warning' or 'error'
 * @param  {Object} parameters
 * @param  {String} parameters.filename
 * @param  {String} parameters.message
 * @param  {Error|Object} parameters.data? - Error instance or additional object data
 * @param  {Boolean} parameters.sendToSentry?
 * @public
 */
const handle = function () {
  let type = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'warning';
  let {
    filename = 'Filename unknown',
    message = 'An exception has occurred.',
    data = null,
    sendToSentry = Boolean(type === 'error')
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const getExtras = error => {
    const obj = {
      filename,
      message,
      errorMessage: error.message || 'Unknown error'
    };
    if (Utils.isObject(data)) {
      obj.extras = {
        ...data
      };
    }
    return obj;
  };
  const error = data instanceof Error ? data : new Error(message);
  const extras = getExtras(error);

  // Log to Sentry
  if (sendToSentry) {
    captureException(error, extras);
  }

  // Busuu logs
  Utils.log(filename, message, data);
  setHTMLtoAppConsole(extras.errorMessage);
};

/**
 * Add additional user context to Sentry
 * tracking.
 * @param {Object} user
 * @public
 */
const identifyUser = user => {
  if (!user) {
    throw new Error(`[File: ${filename$4}] User is missing.`);
  }
  if (isSentryInitialised()) {
    const userContext = getUserContext(user);
    setUser(userContext);
  }
};

/**
 * Initialise Sentry
 * @param {Object} params
 * @param {String} params.key - Sentry key
 * @param {Boolean} params.enableLocal - Enable Sentry in dev mode
 * @param {Boolean} params.sentryOptions - https://docs.sentry.io/error-reporting/configuration/?platform=browsernpm
 */
const init = function () {
  let {
    key = null,
    enableLocal = false,
    sentryOptions = {}
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  if (!key) {
    throw new Error(`[File: ${filename$4}] Key is missing.`);
  }
  const config = getConfig(key, enableLocal, sentryOptions);
  init$2(config);
  initialised = true;
};
var exceptionService = {
  handle,
  identifyUser,
  getSentryIntegrationInstance,
  init
};

/* eslint-disable consistent-return */

/**
 * Clear localStorage and sessionStorage.
 */
const clear = () => {
  try {
    window.localStorage.clear();
    window.sessionStorage.clear();
  } catch (e) {
    // Silence
  }
};

/**
 * Returns an associate data Object for a given key.
 * @param  {String} key
 * @return {Object}
 */
const getConstantKeyData = key => {
  const exist = BrowserStorageConstant.hasOwnProperty(key);
  if (!exist) {
    throw new Error(`Key '${key}' has not been found on browser storage constants list.`);
  }
  return BrowserStorageConstant[key];
};

/**
 * Returns all the storage keys. Both LS and ST.
 * N.B: We freeze the values to avoid any other scripts
 * to change the constants.
 * @return {Object}
 */
const getAll = () => {
  const keys = BrowserStorageConstant;
  const allKeys = {};
  for (const key in keys) {
    if (keys.hasOwnProperty(key)) {
      allKeys[key] = Object.freeze(keys[key]);
    }
  }
  return Object.freeze(allKeys);
};

/**
 * Returns a given item in browser storage (LS or ST) from a key value
 * @param  {String} key
 * @return {String}
 */
const get$1 = key => {
  const keyData = getConstantKeyData(key) || {};
  const {
    type
  } = keyData;
  key = keyData.key;
  if (!type || !key) {
    throw new Error('Key or type is missing.');
  }
  try {
    switch (type) {
      case 'LS':
        return window.localStorage.getItem(key) || null;
      case 'ST':
        return window.sessionStorage.getItem(key) || null;
    }
  } catch (e) {
    // Silence
  }
};

/**
 * Set a given key/value in the localStorage
 * @param  {String} key
 * @param  {String} value
 */
const set$1 = (key, value) => {
  const keyData = getConstantKeyData(key) || {};
  const {
    type
  } = keyData;
  value = keyData.mandatoryValue ? value : key;
  key = keyData.key;
  if (!type || !value || !key) {
    throw new Error(`Key, type or value is missing: key: ${key}, type: ${type}, value: ${value}.`);
  }
  try {
    switch (type) {
      case 'LS':
        window.localStorage.setItem(key, value);
        break;
      case 'ST':
        window.sessionStorage.setItem(key, value);
        break;
    }
  } catch (e) {
    // Silence
  }
};

/**
 * Delete a given item in localStorage from a key value
 * @param  {String} key
 */
const remove$1 = key => {
  const keyData = getConstantKeyData(key) || {};
  const {
    type
  } = keyData;
  key = keyData.key;
  if (!type || !key) {
    throw new Error('Key or type is missing.');
  }
  try {
    switch (type) {
      case 'LS':
        window.localStorage.removeItem(key);
        break;
      case 'ST':
        window.sessionStorage.removeItem(key);
        break;
    }
  } catch (e) {
    // Silence
  }
};
var BrowserStorageService = {
  get: get$1,
  getAll,
  set: set$1,
  clear,
  remove: remove$1
};

/**
 * Stores captcha token if found as url query param
 * @private
 */
const storeCaptchaBypassToken = () => {
  const captchaToken = Utils.getParameter('captcha_token');
  if (captchaToken) {
    BrowserStorageService.set('CAPTCHA_TOKEN', captchaToken);
  }
};

/**
 * A service to provide data storage using cookies.
 * NB: Only use sporadically, BrowserStorageService is preferred when applicable,
 * but localStorage/sessionStorage doesn't apply cross sub-domain
 * (for eg between www.busuu.com and live.busuu.com)
 */

/**
 * Sets a cookie
 * @param {String} name
 * @param {String} value
 * @param {Object} opts
 */
const setCookie = function (name, value) {
  let opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const domain = '.busuu.com';
  Cookies.set(name, value, {
    sameSite: 'strict',
    domain,
    ...opts
  });
};

/**
 * Gets a cookie
 * @param {String} name
 */
const getCookie = name => {
  return Cookies.get(name);
};

/**
 * Removes a cookie
 * @param {String} name
 */
const removeCookie = name => {
  Cookies.remove(name);
};
var cookiesStorageService = {
  setCookie,
  getCookie,
  removeCookie
};

const filename$3 = 'countries-service.js';

/**
 * Returns the country object for the selected country code
 * @param {String} 2 letter country code
 * @returns {Object} Country details { name, code, callingCode }
 */
const getCountryDataByCode = countryCode => {
  const data = CountriesConstants[countryCode];
  if (!data) {
    throw new Error(`[File: ${filename$3}] Could not get country details for the following code: ${countryCode}`);
  }
  return data;
};

/**
 * Returns the phone country code for the selected country in the view
 */
const getSelectedCountryPhoneCode = countryCode => {
  const selectedCountry = getCountryDataByCode(countryCode);
  return selectedCountry ? `+${selectedCountry.callingCode}` : '';
};

/**
 * Gets the shared common country list with label, country code and calling code
 * @returns {Object} Countries object
 */
const getCountryList = () => Utils.deepClone(CountriesConstants);

/**
 * @param {Array} Unsorted country list with translated labels
 * NB: Sample country object within the given array: { code: "GR", label: "Grèce", phoneLabel: "Grèce (+30)" }
 * @param {String} Interface language code
 * @returns {Object} Sorted by the object's label key
 * NB: We use `localeCompare` to ensure that letters with
 * accents aren't ignored and pushed to the end of the array
 * during sorting as `sort` only supports English characters.
 * https://www.jstips.co/en/javascript/sorting-strings-with-accented-characters/
 */
const sortByLabel = (countryList, interfaceLang) => {
  return countryList.sort((a, b) => a.label.localeCompare(b.label, [interfaceLang]));
};

/**
 * Returns if a country is GDPR protected. Defaults to `true`.
 * @param {string} countryCode
 * @returns {boolean}
 */
const isCountryGDPRProtected = countryCode => {
  return countryCode ? GDPR_COUNTRY_CODES.includes(countryCode) : true;
};
var countriesService = {
  getCountryDataByCode,
  getSelectedCountryPhoneCode,
  getCountryList,
  sortByLabel,
  isCountryGDPRProtected
};

/**
 * Initialises the locale to use during the sessions of the app.
 * You do not have to set a locale or initialise this file for dayjs()
 * to have a set locale, it defaults to 'en' by default (Which is actually en-US)
 *
 * NB: When all projects work with webpack and support dynamic imports, we can look
 * at importing the locales dynamically.
 */
const allLocales = {
  ar: arLocale,
  de: deLocale,
  en: enLocale,
  'en-US': enUsLocale,
  es: esLocale,
  fr: frLocale,
  id: idLocale,
  it: itLocale,
  ja: jaLocale,
  ko: koLocale,
  pl: plLocale,
  pt: ptLocale,
  ru: ruLocale,
  tr: trLocale,
  vi: viLocale,
  zh: zhLocale
};

/**
 * WeekStart:
 *
 * - DOES: Affect methods such as `startOf('week')` to be Monday
 * - DOES NOT: return index 0 in `get('day')` for Monday
 *
 * Note that here we're manually overriding anything imported in the locales
 * to force the week to start on Monday for all langs/countries.
 * In the future we can look at doing something more intuitive for certain language combinations,
 * for eg en-US, pt-BR would have a value of `weekStart: 0`. Would obviously need
 * to investigate how this affects any components within the app that use these helpers.
 */
const setLocale = function () {
  let {
    lang = 'en'
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const locale = {
    ...allLocales[lang],
    weekStart: 1
  };
  dayjs.locale(locale);
};
var dateTimeLocale_service = {
  setLocale
};

/* eslint-disable no-undef */

// Private vars
const filename$2 = 'dom-utils.service.js';

/**
 * Returns a DOM element that can be appended anywhere
 *
 * NB: The styles should be a string so we don't need to
 * add every single style individually.
 *
 * Usage:
 *     createDOMElement({
 *         type: 'span',
 *         class: 'some-class',
 *         id: 'some-id'
 *         styles: 'padding:2px;line-height:32px;'
 *         text: 'Some inner text'
 *     });
 *
 * @param  {Object} params
 * @return {Element}
 */
const createDOMElement = params => {
  if (!params.type) {
    return ConsoleHelper.log(filename$2, 'createDOMElement() - missing element type.');
  }
  const element = document.createElement(params.type);
  if (params.class) {
    element.className = params.class;
  }
  if (params.id) {
    element.id = params.id;
  }
  if (params.styles) {
    element.style.cssText = params.styles;
  }
  if (params.text) {
    element.innerText = params.text;
  }
  return element;
};

/**
 * Returns a string or an object of css properties from a given element.
 *
 * Usage:
 * retrieveElementStyles('string', el, ['padding-left']);
 *
 * @param  {String} type | type of return we want - can be string or object
 * @param  {Element} element | element we want to target
 * @param  {Array} styles | list of styles ie ['padding-top', 'padding-left']
 * @return {String|Object}
 */
const retrieveElementStyles = function () {
  let type = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'string';
  let element = arguments.length > 1 ? arguments[1] : undefined;
  let styles = arguments.length > 2 ? arguments[2] : undefined;
  if (!element || !styles) {
    return ConsoleHelper.log(filename$2, 'retrieveElementStyles() - missing `element` or `styles` parameter.');
  }
  let styleString = '';
  const styleObject = {};
  const styleMap = window.getComputedStyle(element);
  styles.forEach(value => {
    const propertyValue = styleMap.getPropertyValue(value);
    styleString += `${value}:${propertyValue};`;
    styleObject[value] = propertyValue;
  });
  if (type === 'object') {
    return styleObject;
  }
  return styleString;
};
var domUtilsService = {
  createDOMElement,
  retrieveElementStyles
};

/* eslint-disable func-names */
const filename$1 = 'marketing-service.js';
const UTM_QUERY_PARAMS = ['utm_source', 'utm_campaign', 'utm_group', 'utm_subnetwork', 'utm_term', 'utm_placement', 'utm_medium', 'utm_term', 'utm_content', 'utm_id', 'utm_group'];
const BUSUU_HOSTNAMES = ['www.busuu.com', 'www.boshu.cn', 'org.busuu.com'];
const SEARCH_ENGINES = ['google', 'bing', 'yahoo', 'ask', 'aol', 'wow', 'webcrawler', 'mywebsearch', 'infospace', 'duckduckgo', 'contenko', 'alhea', 'baidu', 'yandex', 'mail.ru', 'so.com', 'sogou'];

/**
 * Returns the param and value associated
 * from a query param.
 * @param  {String} queryParam
 * @return {Object || null}
 * E.G.: 'foo=bar'
 */
const getParamAndValue = queryParam => {
  const sanitizeValue = value => {
    const hasHash = Boolean(value.indexOf('#') > -1);
    return hasHash ? value.split('#')[0] : value;
  };
  const split = queryParam.split('=');
  let param = null;
  let value = null;
  if (split && split.length === 2) {
    param = split[0];
    value = sanitizeValue(split[1]);
  }
  if (param && value) {
    return {
      param,
      value
    };
  }
  return null;
};

/**
 * Returns the query search from a given string.
 * N.B.: We voluntary avoid window.location.search as
 * Angular doesn't support it and returns an Object instead.
 * @param  {String} string
 * @return {String}
 */
const getSearchQuery = string => {
  if (typeof string !== 'string') {
    return null;
  }
  const split = string.split('?');
  return split && split.length === 2 ? split[1] : null;
};

/**
 * Returns current query params from URL.
 * @param  {Array} filter
 * @return {Object || null}
 */
const getMarketingParams = filter => {
  const searchQuery = decodeURIComponent(getSearchQuery(window.location.href));
  if (!searchQuery || searchQuery.trim() === '') {
    return null;
  }
  const searchParams = searchQuery.split('&');
  let marketingParams = null;

  /**
   * If current URL has parameters:
   * For each found parameter (foo=bar, foo=baz, etc.)
   * we decompile the parameter from the value (param=foo, value=bar).
   * Then we store within `marketingParams` each parameter.
   */
  for (let index = 0, {
      length
    } = searchParams; index < length; index++) {
    const queryParam = getParamAndValue(searchParams[index]);
    if (!queryParam) {
      continue;
    }

    /**
     * An optional parameters filter Array can be passed
     * when you want to restrict a restrictive list of
     * parameters you want to get.
     */
    if (filter && Boolean(filter.indexOf(queryParam.param) === -1)) {
      continue;
    }
    marketingParams = marketingParams || {};
    marketingParams[queryParam.param] = queryParam.value;
  }
  return marketingParams;
};

/**
 * Returns the referrer link if it exists.
 * @return {String || null}
 */
const getReferrer = () => {
  return document.referrer && document.referrer.length ? document.referrer : null;
};

/**
 * Returns true when the referrer comes from
 * a search engine.
 * @param {String}
 * @return {Boolean}
 */
const isReferrerFromSEO = referrer => {
  if (!referrer) {
    return false;
  }
  for (let index = 0, {
      length
    } = SEARCH_ENGINES; index < length; index++) {
    const engine = SEARCH_ENGINES[index];
    const match = Boolean(referrer.indexOf(engine) !== -1);
    if (match) {
      return true;
    }
  }
  return false;
};

/**
 * Returns true when the document referrer
 * isn't busuu but external.
 * @param {String}
 * @return {Boolean}
 */
const isReferrerExternal = referrer => {
  if (!referrer) {
    return false;
  }
  let isExternal = true;
  for (let index = 0, {
      length
    } = BUSUU_HOSTNAMES; index < length; index++) {
    const busuuHost = BUSUU_HOSTNAMES[index];
    const match = Boolean(referrer.indexOf(busuuHost) !== -1);
    if (match) {
      isExternal = false;
    }
  }
  return isExternal;
};

/**
 * Returns an Object when suitable marketing
 * data is found.
 * @return {Object}
 */
const getData = function () {
  let marketingData = null;
  try {
    marketingData = getMarketingParams(UTM_QUERY_PARAMS);
  } catch (e) {
    ConsoleHelper.log(filename$1, `Error while getting marketing parameters.`, e);
    return null;
  }
  if (marketingData) {
    return marketingData;
  }
  const referrer = getReferrer();
  if (isReferrerFromSEO(referrer)) {
    return {
      utm_medium: 'organic',
      utm_content: referrer
    };
  }
  if (isReferrerExternal(referrer)) {
    return {
      utm_medium: 'referral',
      utm_content: referrer
    };
  }
  return {
    utm_medium: 'direct'
  };
};
var MarketingService = {
  getData
};

/* eslint-disable no-use-before-define */
const filename = 'ecommerce-origin-service.js';
const getEcommerceOrigin = type => {
  return eCommerceOriginConstants[type];
};

/**
 * Indicates wether or not an eCommerce Origin
 * type exists based on a given type.
 * @param  {string}  type
 * @return {Boolean}
 */
const isTypeDefined = type => {
  const types = Object.keys(eCommerceOriginConstants);
  return Boolean(types.indexOf(type) !== -1);
};

/**
 * Stores an eCommerce Origin type.
 * 1. If an origin storage can't be found,
 *    we delete the previous origin found to avoid
 *    falsy stored values.
 * @param {string} type
 */
const store = type => {
  if (isTypeDefined(type)) {
    BrowserStorageService.set('ECOMMERCE_ORIGIN', type);
  } else {
    ConsoleHelper.log(filename, `store() - eCommerce type is not found, type: ${type}.`);
    remove(); // 1
  }
};

/**
 * Returns an eCommerce Origin value previously stored.
 * @return {string}
 */
const retrieve = () => {
  const type = BrowserStorageService.get('ECOMMERCE_ORIGIN');
  return get(type);
};

/**
 * Returns an eCommerce Origin value from a given type. If no origin is found,
 * and if the utm_medium is not 'direct', it assumes the origin is 'deeplinking'
 * This could be organic traffic, referral from other sites, CRM, CPC. This avoids
 * having events with an unknown origin
 * @param  {string}  type
 * @return {string}
 */
const get = type => {
  let eCommerceOrigin = isTypeDefined(type) ? getEcommerceOrigin(type) : null;
  if (!eCommerceOrigin) {
    const marketingData = MarketingService.getData();
    const utmMedium = marketingData.utm_medium;
    eCommerceOrigin = utmMedium !== 'direct' ? getEcommerceOrigin('EXTERNAL_SOURCES') : 'unknown_origin';
  }
  return eCommerceOrigin;
};
const remove = () => {
  BrowserStorageService.remove('ECOMMERCE_ORIGIN');
};
var ecommerceOriginService = {
  get,
  store,
  retrieve,
  remove
};

/* eslint-disable no-throw-literal */
const FILENAME = 'font-loader-service.js';
const DEFAULT_FONT = 'Nista';
const LATIN_FONT_NAME = 'lt';
const LATIN_LANGUAGES = ['de', 'en', 'en-US', 'es', 'fr', 'id', 'it', 'pt'];
const FONT_WEIGHTS = [400, 700, 800];

/**
 * NB: Here, `latinLanguages` means latin based languages :)
 * that we can load our Nista Grotesk font for which it has 100% support.
 * For all other languages - ar/ja/ko/pl/ru/tr/vi/zh
 * we have custom font stacks that can be found in _fonts.scss in the core.
 * These stacks load system fonts and support both Windows & OSX
 * We expose this function so that it can be called from font-switcher directive in the webapp.
 * @public
 * @return {Array} - The list of supported latin languages.
 */
const getLatinLanguages = () => {
  return Utils.deepClone(LATIN_LANGUAGES);
};
const setFontClass = alphabet => {
  document.documentElement.classList.add(`font-face-${alphabet}`);
};
const onFontsLoadingSuccess = () => {
  setFontClass(LATIN_FONT_NAME); // Latin
  return LATIN_FONT_NAME;
};
const onFontsLoadingError = error => {
  ConsoleHelper.log(FILENAME, `Failed to load: ${error.family} font.`);
  throw {
    type: 'FONT_LOAD_ERROR',
    error
  };
};

/**
 * Loads all given font variants for a given
 * font name and wrap it into one Promise.
 * @param {String} fontName
 * @param {Array} fontWeights
 * @return {Promise}
 */
const loadFontVariants = (fontName, fontWeights) => {
  // NB: If a weight is non existant, it will default to normal.
  const fontPromises = fontWeights.map(weight => new FontFaceObserver(fontName, {
    weight
  }).load());
  return Promise.all(fontPromises);
};

/**
 * Sets and defines the font being used in the app
 * on the document based on a given interface language.
 * This returns a Promise in all cases as for latin languages based,
 * a font need to be loaded.
 * @param {String} interfaceLanguage
 * @return {Promise}
 */
const set = interfaceLanguage => {
  if (getLatinLanguages().includes(interfaceLanguage)) {
    return loadFontVariants(DEFAULT_FONT, FONT_WEIGHTS).then(onFontsLoadingSuccess).catch(onFontsLoadingError);
  }
  setFontClass(interfaceLanguage);
  return Promise.resolve(interfaceLanguage);
};
var fontLoaderService = {
  set,
  getLatinLanguages
};

/* eslint-disable no-undef */
/**
 * Returns the current document vertical scroll in px
 * @return {integer}
 */
const getCurrentScrollPosition = () => {
  return window.scrollY || document.documentElement.scrollTop;
};

/**
 * Returns true if the given element
 * is at least visible (not entirely)
 * @param  {element} element
 * @param  {object} options
 * @param  {integer} options.offsetY
 * @return {boolean}
 */
const isVisibleInViewport = function (element) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    offsetY: 0
  };
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const scrolledFromTop = getCurrentScrollPosition();
  const elementTop = rect.top + scrolledFromTop;
  const elementBottom = rect.bottom + scrolledFromTop - options.offsetY;
  const isVisible = Boolean(elementTop < scrolledFromTop + viewportHeight && elementBottom > scrolledFromTop);
  return isVisible;
};
var positionService = {
  getCurrentScrollPosition,
  isVisibleInViewport
};

/* eslint-disable no-undef */
const isVendorDisabled = () => {
  const htmlPresent = Boolean(document.documentElement.getAttribute('data-vendor') === 'false');
  const queryParamPresent = Boolean(Utils.getParameter('vendor') === 'false');
  return htmlPresent || queryParamPresent;
};
var thirdPartyService = {
  isVendorDisabled
};

export { BASE_USER_MOCK, BrowserStorageConstant, BrowserStorageService, ConsoleHelper, cookiesStorageService as CookiesStorageService, CountriesConstants, countriesService as CountriesService, dateTimeLocale_service as DateTimeLocaleService, dateUtils as DateUtils, domUtilsService as DomUtilsService, ecommerceOriginService as ECommerceOriginService, exceptionService as ExceptionService, fontLoaderService as FontLoaderService, GDPR_COUNTRY_CODES, jsonToFormData as JSONToFormData, MarketingService, positionService as PositionService, scriptLoader as ScriptLoader, scriptSnowplow as ScriptSnowplow, stringHelper as StringHelper, thirdPartyService as ThirdPartyService, timeHelper as TimeHelper, timeoutPromise as TimeoutPromise, Utils, eCommerceOriginConstants, storeCaptchaBypassToken };
//# sourceMappingURL=index.esm.js.map
